/* ==========================================================================
   layaway-form.js — Layby USA checkout controller
   Built against sections/main-cart.liquid (binds by element id).

   Ported from the AU controller (prestige-au-base:assets/page-form.es6.js),
   rewritten vanilla — Hyper ships no jQuery. AU's BSB direct-debit form was
   dropped; step machinery, tax quote, Reg E authorization
   rendering and the 3DS return leg are new.

   PAY AT CHECKOUT (2026-10-05). The first payment is CHARGED when the order is
   placed, and there is no order unless it goes through (Adrian: "take first
   payment immediately on checkout and it doesn't go through unless payment goes
   through"). Submit runs, in order:
     0. elements.submit() — inside the tap itself, so Apple Pay and Google Pay
        can open their sheet (2026-10-08);
     1. create-order {dry_run:true} — every server check, nothing created, and
        the first-payment amount computed from the real prices;
     2. stripe.createConfirmationToken() from a deferred Payment Element;
     3. lad-airtable-us /api/stripe/checkout-charge — the SERVER confirms the
        charge (one PaymentIntent per checkout, keyed on state.checkoutEventId,
        so a retry can never charge twice), with 3-D Secure via handleNextAction;
     4. create-order with the PaymentIntent — verified and claimed before the
        Shopify order exists.
   The charge also saves the card for the rest of the plan. Card type only,
   which still takes Apple Pay, Google Pay and Link: each arrives as a card (a
   Link bank payment as brand 'link', funding 'unknown'), so the server sees the
   funding type of every real card. A decline names the other ways to pay
   (showPayFork); Apple Pay is asked for a plan token (applePayPlan). Payment #1 is today, and
   there is no payday to pick (2026-10-13): the rest come out on today's weekday, every week or
   every 2 weeks (Adrian: "It just pays on their frequency select from that checkout payment day").

   All money math comes from the schedule-data JSON emitted by
   snippets/layaway-plan.liquid — never recompute plan math here.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.LAYAWAY_CONFIG || {};
  // A checkout opened with #bank-test shows the "Bank account" choice before it is on for
  // everyone; the backend still decides who may pay through it (its tester list).
  var BANK_TEST = /(^#|&)bank-test(&|$)/.test(window.location.hash || '');
  // Both payment forms (card, bank) look the same.
  var APPEARANCE = { theme: 'stripe', variables: { colorPrimary: '#e8641b', borderRadius: '6px' } };
  var STORAGE_KEY = 'layaway_form_data';
  var PENDING_KEY = 'ladPendingOrder';
  // The publishable key the CRM served with the SetupIntent, kept across the 3DS
  // redirect — that return lands on a fresh page load with no CRM round-trip, and
  // retrieveSetupIntent must use the SAME account's key or it cannot read the intent.
  var PK_KEY = 'ladStripePk';
  // This checkout's id and, once the first payment succeeds, its PaymentIntent — kept for
  // the tab's life so a 3-D Secure redirect or a reload resumes the SAME checkout.
  var CHECKOUT_KEY = 'ladCheckoutSession';
  var EXIT_KEY = 'layaway_exit_shown';

  var form = document.getElementById('layaway-signup');
  if (!form || form.layawayAbandoned || form.layawayInitializing || form.layawayReady) return;
  form.layawayInitializing = true;

  /* ---------------------------------------------------------------- utils */

  var $ = function (id) { return document.getElementById(id); };
  var money = function (cents) {
    if (cents == null || isNaN(cents)) return '—';
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  var fmtDate = function (d) { return MONTHS[d.getMonth()] + ' ' + d.getDate(); };
  var fmtLong = function (d) { return DAYS[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate(); };
  var DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var fmtShort = function (d) { return DAYS_SHORT[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate(); };
  // Month + day, with the year appended only once the date leaves the current
  // year — a 140-week plan runs into 2029, and "Paid off Mar 17" alone would
  // read as next spring.
  var fmtDateYear = function (d) {
    var y = d.getFullYear();
    return fmtDate(d) + (y !== new Date().getFullYear() ? ', ' + y : '');
  };
  var fmtShortYear = function (d) {
    var y = d.getFullYear();
    return fmtShort(d) + (y !== new Date().getFullYear() ? ', ' + y : '');
  };
  var fmtSlash = function (d) {
    return String(d.getMonth() + 1).padStart(2, '0') + '/' +
      String(d.getDate()).padStart(2, '0') + '/' + d.getFullYear();
  };
  var parseSlash = function (s) {
    var m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || '');
    if (!m) return null;
    var d = new Date(+m[3], +m[1] - 1, +m[2]);
    return isNaN(d.getTime()) ? null : d;
  };
  var addDays = function (d, n) { var x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; };
  var startOfToday = function () { var d = new Date(); d.setHours(0, 0, 0, 0); return d; };

  /* ------------------------------------------------------------ plan data */

  var PLAN = (function () {
    var node = form.querySelector('script[data-layaway-plan]');
    var plan = node && JSON.parse(node.textContent);
    var keys = ['price_cents', 'deposit_cents', 'installment_cents', 'weekly_base_cents',
      'deposit_pct', 'deposit_floor_cents', 'order_cap_cents'];
    if (!plan || !keys.every(function (key) {
      return typeof plan[key] === 'number' && isFinite(plan[key]) && plan[key] >= 0;
    }) || plan.weekly_base_cents <= 0 || plan.order_cap_cents <= 0) {
      throw new Error('Checkout plan data is missing or invalid');
    }
    return plan;
  })();

  // What the customer pays at checkout: the first payment — one regular payment of
  // the plan they picked, or the whole total when the cart is smaller than one
  // payment. Same rule as LAYAWAYUS-VERCEL lib/plan.js firstPaymentCents; the dry
  // run re-computes it from the real prices and the charge uses the server's figure.
  function firstPaymentCents() {
    var n = installmentCount();
    var reg = regularInstallmentCents();
    if (n <= 0) return reg;
    return n === 1 ? financedCents() : Math.min(reg, financedCents());
  }
  var dueTodayLabel = function () { return money(firstPaymentCents()); };
  // The button keeps its amount in a span, so the figure follows the plan even after
  // an error put the label back (setSubmitting restores this markup).
  var submitHtml = function () {
    return 'Pay <span id="submit-deposit">' + dueTodayLabel() + '</span> and start my layaway';
  };

  var state = {
    step: 1,
    frequency: 'weekly',
    // The weekly rate the customer picked, in cents. Seeded from the plan JSON's
    // store default below, once PLAN exists.
    weeklyRateCents: 0,
    taxCents: null,
    taxQuoteId: null,
    stripe: null,
    elements: null,
    publishableKey: null,
    // One id for the whole checkout (kept across a 3-D Secure redirect): the server
    // keys the checkout's one PaymentIntent on it, so a retry can never charge twice.
    checkoutEventId: null,
    // Set once the first payment has succeeded; a re-press then goes straight to the
    // order, never to a second charge.
    paidIntentId: null,
    paymentElement: null,
    // What this device can pay with besides a card (Stripe's own check), and how the last
    // attempt paid ('card', 'apple_pay', 'google_pay', 'link_card', 'link_other'): the
    // "try another way to pay" box offers only the others.
    wallets: { applePay: false, googlePay: false },
    lastMethod: null,
    // Card or bank (2026-10-08): whether the bank choice is offered, which one is showing,
    // and the bank's own payment form (Link), made the first time Bank is picked.
    bankLink: false,
    payDoor: 'card',
    bankElements: null,
    bankElement: null,
    // The day the plan is dated from. Null = today on the Eastern calendar (regionToday); set
    // to the server's checkout day when the two disagree (see the preflight in submit).
    serverDay: null,
    // Payment #2 as last drawn (YYYY-MM-DD): the date the customer is shown and signs, posted
    // as next_pay_date so the server bills that date, not one re-dated after midnight.
    shownSecond: null,
    stripeMounted: false,
    placesBound: false,
    submitting: false
  };

  // Weekly is the base cadence: the plan is $25 a week and PLAN.weeks is the
  // weekly payment count. Other cadences are the SAME total over the SAME
  // elapsed time, just fewer, larger payments — the count and amount are both
  // derived from cadence days so the arithmetic cannot drift from the plan.
  // Every cadence is a whole number of WEEKS. Semi-monthly (24/yr) never lines up with a
  // 7-day grid and was removed 2026-07-28; calendar-monthly is out for the same reason
  // plus two more — a 30-day step drifts the weekday, so a plan that starts on a payable
  // Wed/Thu/Fri would wander onto Mon/Tue and weekends, and $25 x (30/7) is 10714.2857
  // cents, which the server's own sum assertion rejects. "Every 4 weeks" (28 days) was
  // sold until 2026-10-05; checkout now offers weekly or fortnightly only, and
  // create-order refuses anything else on the pay-at-checkout path.
  var CADENCE = {
    weekly: { days: 7, label: 'every week' },
    biweekly: { days: 14, label: 'every 2 weeks' }
  };

  /**
   * The weekly rates on offer, from the plan JSON (snippets/layaway-plan.liquid),
   * which mirrors LAYAWAYUS-VERCEL/lib/plan.js ALLOWED_WEEKLY_CENTS.
   *
   * A cart cached before the selector shipped carries no list; it falls back to the
   * single store rate, which is exactly what that page already showed and what the
   * server still defaults to. The chip row then renders one chip rather than none —
   * an empty row would read as a broken step with nothing to pick.
   */
  var RATES = (function () {
    var list = PLAN.allowed_weekly_cents;
    var ok = Array.isArray(list) && list.filter(function (c) {
      return typeof c === 'number' && isFinite(c) && c > 0 && c === Math.round(c);
    });
    return (ok && ok.length ? ok : [PLAN.weekly_base_cents]).sort(function (a, b) { return a - b; });
  })();

  // The store default if it is on offer, otherwise the smallest rate — never a value
  // the server would refuse.
  state.weeklyRateCents = RATES.indexOf(PLAN.weekly_base_cents) >= 0 ? PLAN.weekly_base_cents : RATES[0];

  /* --------------------------------------------------------- field errors */

  var ERROR_MAP = {
    'order-form-register-email': 'email-error',
    'order-form-register-phone': 'phone-error',
    'order-form-register-f-name': 'fname-error',
    'order-form-register-l-name': 'lname-error',
    'dob': 'dob-error',
    'order-form-register-street-address': 'address-error',
    'order-form-register-city': 'city-error',
    'province': 'state-error',
    'order-form-register-zip': 'zip-error',
    'order-form-weekly-rate': 'rate-error',
    'order-form-pay-frequency': 'frequency-error',
    'typed-signature': 'signature-error',
    'agreed_to_terms': 'terms-error',
    // the gift — validated only while the toggle is on
    'gift-f-name': 'gift-fname-error',
    'gift-l-name': 'gift-lname-error',
    'gift-phone': 'gift-phone-error',
    'gift-street-address': 'gift-address-error',
    'gift-city': 'gift-city-error',
    'gift-province': 'gift-state-error',
    'gift-zip': 'gift-zip-error'
  };

  function setError(fieldId, message) {
    // A message can be aimed at an error span directly (the signature pad's 'sigpad-error'
    // has no field of its own); a bare id that is not in the map is that span.
    var span = $(ERROR_MAP[fieldId] || fieldId);
    if (span) span.textContent = message || '';
    var el = $(fieldId);
    if (el && el.classList) el.classList.toggle('has-error', !!message);
  }
  function clearErrors() {
    Object.keys(ERROR_MAP).forEach(function (id) { setError(id, ''); });
  }

  /* ----------------------------------------------------------- validation */

  var RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function validateField(id) {
    var el = $(id);
    if (!el) return true;
    var v = (el.value || '').trim();

    switch (id) {
      case 'order-form-register-email':
        if (!v) return setError(id, 'Enter your email so we can send your plan.'), false;
        if (!RE_EMAIL.test(v)) return setError(id, 'That email doesn\'t look right.'), false;
        break;
      case 'order-form-register-phone': {
        if (!v) return setError(id, 'Enter your mobile number.'), false;
        // Real NANP shape, matching the server: area code and exchange start
        // 2-9. "10 digits" alone accepts numbers no US carrier can issue (an
        // NZ 027... mobile is 10 digits) and the failure then surfaces as a
        // generic server error at the very last step.
        var pd = v.replace(/\D/g, '');
        if (pd.length === 11 && pd.charAt(0) === '1') pd = pd.slice(1);
        if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(pd)) {
          return setError(id, 'Enter a valid US mobile number \u2014 10 digits, area code starting 2\u20139.'), false;
        }
        break;
      }
      case 'order-form-register-f-name':
        if (v.length < 2) return setError(id, 'Enter your first name.'), false;
        break;
      case 'order-form-register-l-name':
        if (v.length < 2) return setError(id, 'Enter your last name.'), false;
        break;
      case 'dob': {
        var d = parseSlash(v);
        if (!d) return setError(id, 'Use MM/DD/YYYY.'), false;
        var age = (Date.now() - d.getTime()) / 31557600000;
        if (age < 18) return setError(id, 'You must be 18 or older to start a plan.'), false;
        if (age > 110) return setError(id, 'Check your date of birth.'), false;
        break;
      }
      case 'order-form-register-street-address':
        if (v.length < 4) return setError(id, 'Enter your street address.'), false;
        break;
      case 'order-form-register-city':
        if (v.length < 2) return setError(id, 'Enter your city.'), false;
        break;
      case 'province':
        if (!v) return setError(id, 'Choose your state.'), false;
        // Compare the CODE, not the value — values are full names, so the old
        // `v === 'DC'` test never matched anything.
        if (ALLOWED_STATE_CODES.indexOf(selectedStateCode()) === -1) {
          return setError(id, 'We can\'t serve this state yet.'), false;
        }
        break;
      case 'order-form-register-zip':
        if (!/^\d{5}$/.test(v)) return setError(id, 'Enter a 5-digit ZIP code.'), false;
        break;
      case 'typed-signature':
        if (v.length < 3) return setError(id, 'Type your full name to sign.'), false;
        if (sigPad && !sigPad.hasInk()) {
          setError('sigpad-error', 'Please sign in the box above.');
          return false;
        }
        break;
      case 'agreed_to_terms':
        if (!el.checked) return setError(id, 'Please check this box to continue.'), false;
        break;

      // ---- the gift. Same shapes as the payer's own fields; different words.
      case 'gift-f-name':
        if (v.length < 2) return setError(id, 'Enter their first name.'), false;
        break;
      case 'gift-l-name':
        if (v.length < 2) return setError(id, 'Enter their last name.'), false;
        break;
      case 'gift-phone': {
        if (!v) return setError(id, 'Enter their mobile number, for the courier.'), false;
        var gd = v.replace(/\D/g, '');
        if (gd.length === 11 && gd.charAt(0) === '1') gd = gd.slice(1);
        if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(gd)) {
          return setError(id, 'Enter a valid US mobile number \u2014 10 digits, area code starting 2\u20139.'), false;
        }
        break;
      }
      case 'gift-street-address':
        if (v.length < 4) return setError(id, 'Enter their street address.'), false;
        break;
      case 'gift-city':
        if (v.length < 2) return setError(id, 'Enter their city.'), false;
        break;
      case 'gift-province':
        if (!v) return setError(id, 'Choose their state.'), false;
        if (ALLOWED_STATE_CODES.indexOf(codeOf('gift-province')) === -1) {
          return setError(id, 'We can\'t deliver to this state yet.'), false;
        }
        break;
      case 'gift-zip':
        if (!/^\d{5}$/.test(v)) return setError(id, 'Enter a 5-digit ZIP code.'), false;
        break;
    }
    setError(id, '');
    return true;
  }

  /** The USPS code behind whichever state <select> is asked about. */
  function codeOf(selectId) {
    var sel = $(selectId);
    if (!sel || sel.selectedIndex < 0) return '';
    return sel.options[sel.selectedIndex].getAttribute('data-code') || '';
  }

  function giftOn() {
    var t = $('gift-toggle');
    return !!(t && t.checked);
  }

  var STEP_FIELDS = {
    1: ['order-form-register-email', 'order-form-register-phone'],
    2: ['order-form-register-f-name', 'order-form-register-l-name', 'dob',
        'order-form-register-street-address', 'order-form-register-city', 'province', 'order-form-register-zip'],
    3: [],
    4: ['agreed_to_terms', 'typed-signature'],
    gift: ['gift-f-name', 'gift-l-name', 'gift-phone', 'gift-street-address', 'gift-city', 'gift-province', 'gift-zip']
  };

  function validateStep(n) {
    var ok = true, firstBad = null;
    STEP_FIELDS[n].forEach(function (id) {
      if (!validateField(id)) { ok = false; if (!firstBad) firstBad = id; }
    });
    if (!ok && firstBad) {
      var el = $(firstBad);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el.focus) try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
      }
    }
    return ok;
  }

  /* ------------------------------------------------------------- sections

     The form was four steps behind three Continue buttons. It is one page
     now, so nothing is ever hidden and there is nothing to advance TO: what
     is left of goToStep is scrolling to a section, which is what the Stripe
     error paths actually wanted when they asked for step 4.

     The step numbers stay because validation is still grouped by them, and
     because the fieldsets are still sections a customer reads in an order.
  */

  function goToStep(n) {
    state.step = n;
    if (checkoutCapture) checkoutCapture.schedule();
    var fs = form.querySelector('.layaway-step[data-step="' + n + '"]');
    var top = (fs || form).getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: top, behavior: 'smooth' });
    persist();
  }

  /* -------------------------------------------------------------- masking */

  function bindMask(id, fn) {
    var el = $(id);
    if (!el) return;
    el.addEventListener('input', function () {
      var pos = el.selectionStart === el.value.length;
      el.value = fn(el.value);
      if (pos) el.selectionStart = el.selectionEnd = el.value.length;
    });
  }
  bindMask('dob', function (v) {
    var d = v.replace(/\D/g, '').slice(0, 8);
    if (d.length > 4) return d.slice(0, 2) + '/' + d.slice(2, 4) + '/' + d.slice(4);
    if (d.length > 2) return d.slice(0, 2) + '/' + d.slice(2);
    return d;
  });
  bindMask('order-form-register-phone', function (v) {
    var d = v.replace(/\D/g, '').slice(0, 10);
    if (d.length > 6) return '(' + d.slice(0, 3) + ') ' + d.slice(3, 6) + '-' + d.slice(6);
    if (d.length > 3) return '(' + d.slice(0, 3) + ') ' + d.slice(3);
    if (d.length) return '(' + d;
    return d;
  });
  bindMask('order-form-register-zip', function (v) { return v.replace(/\D/g, '').slice(0, 5); });

  // inline validation on blur
  Object.keys(ERROR_MAP).forEach(function (id) {
    var el = $(id);
    if (!el) return;
    var evt = (el.type === 'checkbox' || el.tagName === 'SELECT') ? 'change' : 'blur';
    el.addEventListener(evt, function () { validateField(id); persist(); });
  });

  /* --------------------------------------- excluded-jurisdiction gating */

  // Since the 2026-09-11 terms we serve FIVE states (§10/§16): the <select>
  // lists only those, which is the real block; this gate is the belt to that
  // braces, and it is an ALLOW-list so an unexpected code fails closed.
  //
  // It reads data-code, NOT sel.value. The option values are FULL NAMES
  // ("Ohio"), so the previous `sel.value === 'DC' || sel.value === 'MD'` could
  // never be true and this gate had never fired once. Nothing depended on it
  // only because the omission above was doing the work.
  var ALLOWED_STATE_CODES = JSON.parse(form.querySelector('[data-layaway-states]').textContent);
  if (!Array.isArray(ALLOWED_STATE_CODES) || !ALLOWED_STATE_CODES.length) {
    throw new Error('Checkout eligibility data is missing');
  }

  function selectedStateCode() {
    var sel = $('province');
    if (!sel || sel.selectedIndex < 0) return '';
    var opt = sel.options[sel.selectedIndex];
    return (opt && opt.getAttribute('data-code')) || '';
  }

  /**
   * Set the state <select> from a USPS code, via each option's data-code.
   *
   * Returns false when no option carries that code — which is exactly what
   * happens for every state outside the five we serve, because only those five
   * are in the markup. The dropdown IS the allowlist, so "Google returned a
   * state we do not stock an option for" and "we do not serve that state" are
   * the same condition, and there is no second list to keep in step.
   */
  function setStateByCode(code, selectId) {
    code = String(code || '').trim().toUpperCase();
    var sel = $(selectId || 'province');
    if (!sel || !code) return false;
    for (var i = 0; i < sel.options.length; i++) {
      if (ALLOWED_STATE_CODES.indexOf(code) !== -1 && sel.options[i].getAttribute('data-code') === code) {
        sel.selectedIndex = i;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  /**
   * A state we cannot serve used to disable the Continue button between
   * step 2 and step 3. There is no Continue any more, so it disables the one
   * button left — alongside the order cap, which blocks the same button for a
   * different reason and must not be cleared by this one.
   */
  function applySubmitBlocks() {
    var btn = $('btn-submit');
    if (btn) btn.disabled = !!(!form.layawayReady || form.layawayAbandoned || state.submitting || state.stateBlocked || state.capBlocked || state.emptyCart || (giftOn() && state.giftStateBlocked));
  }

  function checkStateGate() {
    var sel = $('province');
    var msg = $('state-gate-message');
    var code = selectedStateCode();
    var blocked = !!(sel && (sel.dataset.unsupportedState || (sel.value && ALLOWED_STATE_CODES.indexOf(code) === -1)));
    if (msg) msg.hidden = !blocked;
    state.stateBlocked = blocked;
    applySubmitBlocks();
    return !blocked;
  }
  if ($('province')) $('province').addEventListener('change', function () {
    if (selectedStateCode()) delete $('province').dataset.unsupportedState;
    checkStateGate(); maybeQuoteTax();
  });
  if ($('order-form-register-zip')) $('order-form-register-zip').addEventListener('blur', maybeQuoteTax);

  /* -------------------------------------------- Google Places autocomplete */

  /**
   * Address autocomplete, ported from the AU controller and restricted to the US.
   *
   * Differences from AU that matter:
   *   - componentRestrictions country 'us'.
   *   - The state <select> stores FULL NAMES as option values while Google
   *     returns a short_name ('OH'), so the code is matched against each
   *     option's data-code rather than assigned to .value.
   *   - An excluded jurisdiction has no option at all, so a Google pick in any
   *     state we do not serve lands on the state gate instead of silently leaving the
   *     select empty. That is the case AU never had.
   *   - subpremise goes to the Apt/Unit field, which exists separately here,
   *     rather than being folded into street address.
   *
   * Every write dispatches input/change so the existing validation and the
   * sales-tax quote (bound to province change and zip blur) run exactly as they
   * do when a customer types.
   */
  /**
   * Shut the suggestion list after a pick.
   *
   * Places owns `.pac-container`'s inline display and sets it back to block on
   * the next real keystroke, so hiding it here is not sticky. Belt to the
   * silent-write braces above: the container can also linger when a pick is
   * made by mouse, because the blur that would normally close it never happens.
   */
  function closeSuggestions(inputId) {
    var input = $(inputId || 'order-form-register-street-address');
    var boxes = document.querySelectorAll('.pac-container');
    for (var i = 0; i < boxes.length; i++) boxes[i].style.display = 'none';
    if (input) input.blur();
  }

  /**
   * One autocomplete per address block. This was a singleton wired to the
   * payer's field ids; the gift needs a second one, so it takes a map of ids
   * and keeps its own bound flag. The two differ in exactly one place: whose
   * state an excluded pick blocks. A payer in Georgia cannot open a plan; a box
   * to Georgia cannot be sent. Both stop the order, but they say different things.
   */
  var PAYER_PLACES = {
    key: 'payer', street: 'order-form-register-street-address', unit: 'order-form-register-street-address-2',
    city: 'order-form-register-city', state: 'province', zip: 'order-form-register-zip',
    gateMsg: 'state-gate-message', blockedText: 'We can\'t serve ',
    onBlocked: function () { state.stateBlocked = true; },
    afterPick: function () { checkStateGate(); maybeQuoteTax(); }
  };
  var GIFT_PLACES = {
    key: 'gift', street: 'gift-street-address', unit: 'gift-street-address-2',
    city: 'gift-city', state: 'gift-province', zip: 'gift-zip',
    gateMsg: 'gift-state-gate-message', blockedText: 'We can\'t deliver to ',
    onBlocked: function () { state.giftStateBlocked = true; },
    afterPick: function () { checkGiftStateGate(); }
  };
  state.placesBoundFor = {};

  function bindPlaces() {
    bindPlacesTo(PAYER_PLACES);
    if (giftOn()) bindPlacesTo(GIFT_PLACES);
  }

  function bindPlacesTo(cfg) {
    if (state.placesBoundFor[cfg.key]) return;
    var input = $(cfg.street);
    if (!input || !window.google || !google.maps || !google.maps.places) return;

    var autocomplete = new google.maps.places.Autocomplete(input, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['address_components', 'formatted_address'],
    });

    // Enter selects a suggestion; it must not submit the step.
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') e.preventDefault();
    });

    autocomplete.addListener('place_changed', function () {
      var place = autocomplete.getPlace();
      if (!place || !place.address_components) return;

      var part = {};
      place.address_components.forEach(function (c) {
        c.types.forEach(function (t) { part[t] = { long: c.long_name, short: c.short_name }; });
      });

      var streetNumber = part.street_number ? part.street_number.long : '';
      var route = part.route ? part.route.long : '';
      var city = (part.locality || part.sublocality_level_1 || part.postal_town || {}).long || '';
      var stateCode = (part.administrative_area_level_1 || {}).short || '';
      var zip = (part.postal_code || {}).long || '';
      var unit = (part.subpremise || {}).long || '';

      /**
       * `silent` suppresses the `input` event.
       *
       * Places listens for `input` on the address field. Writing the tidied
       * street back and dispatching `input` looks to Places exactly like the
       * customer typing, so it re-queries and re-opens the suggestion list —
       * which then sits there after a pick and will not go away. Only the
       * address input needs this; the others are not watched.
       *
       * `change` still fires, which is what validation and the tax quote use.
       */
      var setVal = function (id, val, silent) {
        var el = $(id);
        if (!el || !val) return;
        el.value = val;
        if (!silent) el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      };

      // Street line without the unit — the Apt/Unit field is its own input.
      var street = (streetNumber + ' ' + route).trim();
      if (street) setVal(cfg.street, street, true);
      if (unit) setVal(cfg.unit, unit);
      setVal(cfg.city, city);
      setVal(cfg.zip, zip);

      var placed = setStateByCode(stateCode, cfg.state);
      if (!placed) {
        // No option for this state => a jurisdiction we do not serve. Say so
        // rather than leaving the customer with an empty State field and no
        // idea why their address did not stick.
        var sel = $(cfg.state);
        if (sel) {
          sel.selectedIndex = 0;
          sel.dataset.unsupportedState = stateCode || 'unknown';
          sel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        var msg = $(cfg.gateMsg);
        if (msg) msg.hidden = false;
        setError(cfg.state, cfg.blockedText + (stateCode || 'this address') + ' yet.');
        cfg.onBlocked();
        applySubmitBlocks();
        closeSuggestions(cfg.street); // this path returns early — it needs the close too
        return;
      }

      closeSuggestions(cfg.street);
      cfg.afterPick();
      persist();
    });

    state.placesBoundFor[cfg.key] = true;
  }

  /** Mirrors checkStateGate for the box's destination. */
  function checkGiftStateGate() {
    var msg = $('gift-state-gate-message');
    var giftCode = codeOf('gift-province');
    var blocked = !!(giftOn() && ($('gift-province').dataset.unsupportedState || ($('gift-province').value && ALLOWED_STATE_CODES.indexOf(giftCode) === -1)));
    if (msg) msg.hidden = !blocked;
    state.giftStateBlocked = blocked;
    applySubmitBlocks();
    return !blocked;
  }
  if ($('gift-province')) $('gift-province').addEventListener('change', function () {
    if (codeOf('gift-province')) delete $('gift-province').dataset.unsupportedState;
    checkGiftStateGate();
  });

  /**
   * Show or hide the gift fields. Off clears their errors and their gate, so
   * a recipient address someone abandoned can never block the order.
   */
  function syncGift() {
    var on = giftOn();
    var body = $('gift-fields');
    if (body) body.hidden = !on;
    if (on) {
      bindPlacesTo(GIFT_PLACES);
      checkGiftStateGate();
    } else {
      STEP_FIELDS.gift.forEach(function (id) { setError(id, ''); });
      var msg = $('gift-state-gate-message');
      if (msg) msg.hidden = true;
      state.giftStateBlocked = false;
      applySubmitBlocks();
    }
  }
  if ($('gift-toggle')) $('gift-toggle').addEventListener('change', function () { syncGift(); persist(); });

  // Maps may resolve before or after this controller runs, so handle both.
  window.initGooglePlaces = function () { window.__placesReady = true; bindPlaces(); };
  if (window.__placesReady) bindPlaces();

  /* ------------------------------------------------------------ chip rows */

  function bindChips(rowId, hiddenId, onPick) {
    var row = $(rowId);
    if (!row) return;
    row.addEventListener('click', function (e) {
      var chip = e.target.closest('.layaway-chip');
      if (!chip || chip.dataset.value === undefined) return;
      row.querySelectorAll('.layaway-chip').forEach(function (c) { c.classList.remove('is-selected'); });
      chip.classList.add('is-selected');
      var hidden = $(hiddenId);
      if (hidden) hidden.value = chip.dataset.value;
      setError(hiddenId, '');
      if (onPick) onPick(chip.dataset.value);
      persist();
    });
  }

  /* ----------------------------------------------------------- rate chips */

  /**
   * The weekly rate: $15, $25 or $50. Rendered from RATES rather than written into
   * main-cart.liquid so the amounts on screen and the amounts create-order will
   * accept come from one list.
   */
  function renderRateChips() {
    var row = $('rate-selector');
    if (!row) return;
    row.innerHTML = RATES.map(function (cents) {
      return '<button type="button" class="layaway-chip' +
        (cents === state.weeklyRateCents ? ' is-selected' : '') +
        '" data-value="' + cents + '">' + moneyShort(cents) + '</button>';
    }).join('');
    var hidden = $('order-form-weekly-rate');
    if (hidden) hidden.value = String(state.weeklyRateCents);
    renderPlanHeadline();
  }

  /**
   * The plan, read back as one sentence at the top of the panel: "$25 every Wednesday",
   * then what that buys and when it ends. It moves with any of the three choices under
   * it (renderSchedule runs on every one), and before a day is picked it says so rather
   * than guessing. The short final payment is named here as it is on the timeline: most
   * carts do not divide by the rate, and a plan quoted by its regular payment alone would
   * read as one that does not sum to the cart.
   */
  function planCountLine() {
    var n = installmentCount();
    if (!n) return '';
    var amt = perInstallmentCents(), fin = finalInstallmentCents();
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    // "19 payments of $25 every week": amount before cadence, the order the headline uses.
    var regular = function (count) { return count + ' payment' + (count === 1 ? '' : 's') + ' of ' + moneyShort(amt) + ' ' + cad.label; };
    if (n === 1) return 'a single payment of ' + moneyShort(fin);
    if (fin !== amt) return regular(n - 1) + ', then a final ' + moneyShort(fin);
    return regular(n);
  }

  function renderPlanHeadline() {
    var head = $('plan-headline'), sub = $('plan-subline');
    if (!head || !sub) return;
    var count = planCountLine();
    if (!count) { head.textContent = ''; sub.textContent = ''; return; }
    var n = installmentCount();
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    // Every payment falls on today's weekday (the Eastern day the server dates #1 by).
    var day = DAYS[planDay().getDay()];
    var every = cad.days === 7 ? 'every ' + day : cad.label + ' on a ' + day;
    // "$25 today, then $25 every Wednesday": today's payment first, because it is the
    // one taken at checkout.
    head.textContent = '';
    var strong = document.createElement('strong');
    strong.textContent = moneyShort(firstPaymentCents()) + ' today';
    head.appendChild(strong);
    head.appendChild(document.createTextNode(n === 1 ? ' — paid in full' : ', then ' + moneyShort(perInstallmentCents()) + ' ' + every));
    var dates = scheduleDates();
    if (n === 1) { sub.textContent = 'One payment, taken when you place your order.'; return; }
    var fin = finalInstallmentCents(), later = n - 1;
    sub.textContent = dates.length > 1
      ? 'Today ' + moneyShort(firstPaymentCents()) + ' · then ' + later + ' more payment' + (later === 1 ? '' : 's') +
        ' from ' + fmtShort(dates[1]) + (fin !== perInstallmentCents() ? ' (the last one ' + moneyShort(fin) + ')' : '') +
        ' · Paid off ' + fmtDateYear(dates[dates.length - 1])
      : count.charAt(0).toUpperCase() + count.slice(1) + '. The first is taken today.';
  }

  bindChips('rate-selector', 'order-form-weekly-rate', function (v) {
    var cents = parseInt(v, 10);
    // Never take a rate that is not on offer: the chip row is the only way to set
    // this, but the hidden input is restored from localStorage and a cart saved
    // before a rate was retired would otherwise reach create-order and be refused.
    if (RATES.indexOf(cents) < 0) return;
    state.weeklyRateCents = cents;
    // The rate changes what each payment is, not which days we debit. replan re-derives
    // the term and moves the totals, the authorization block and the timeline with it.
    replan(window.cartData);
  });

  bindChips('frequency-selector', 'order-form-pay-frequency', function (v) {
    state.frequency = v;
    renderSchedule();
  });

  /* ------------------------------------------------------- the debit day */

  /**
   * The calendar the debits run on. The CRM does its US date arithmetic in Eastern time
   * (US_TIMEZONE in us/api/_shared/env.ts) and the order backend dates payment #1 on the
   * same clock, so the schedule here must too: not UTC, and not the customer's browser.
   */
  var REGION_TIME_ZONE = 'America/New_York';

  /**
   * The region's calendar day, as a local-midnight Date.
   *
   * The debit calendar belongs to the region, not to whoever is looking at the page: a
   * customer signing up at 11pm Eastern on a Wednesday pays on Wednesday, and then every
   * Wednesday, whatever their laptop's clock says. The browser's own date is only the
   * fallback for an engine without time-zone data.
   */
  function regionToday() {
    var now = new Date();
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: REGION_TIME_ZONE, year: 'numeric', month: 'numeric', day: 'numeric'
      }).formatToParts(now);
      var part = function (type) {
        for (var i = 0; i < parts.length; i++) if (parts[i].type === type) return +parts[i].value;
        return NaN;
      };
      var d = new Date(part('year'), part('month') - 1, part('day'));
      if (!isNaN(d.getTime())) return d;
    } catch (e) { /* unknown zone or no Intl: fall through to the browser's day */ }
    return startOfToday();
  }

  /** The day the plan is dated from: the server's checkout day once it has said so, else today. */
  function planDay() {
    return state.serverDay || regionToday();
  }

  /** YYYY-MM-DD, the server's date format. */
  function isoDay(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  /* ------------------------------------------------- schedule computation */

  /**
   * The regular installment: the weekly base times the cadence multiple.
   * Weekly $25, fortnightly $50 — always round, never a derived fraction.
   */
  function regularInstallmentCents() {
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    // The CHOSEN rate, not PLAN.installment_cents. Those were the same number while
    // the store sold one rate; reading the plan JSON here after the selector shipped
    // would quote $25 to a customer who picked $15.
    var base = state.weeklyRateCents || PLAN.installment_cents || 0;
    return base * (cad.days / 7);
  }

  /**
   * The amount the schedule must cover: sticker plus quoted tax, minus deposit.
   * taxCents stays in the arithmetic even though no tax is charged today (the
   * authorization copy makes no claim about tax in either direction):
   * if a real quote ever lands, the payments still have to sum to the server's
   * financed total (which finances tax) or verifyClientPlan refuses the order
   * as a PLAN_MISMATCH. Whoever wires up a tax provider must change the
   * authorization copy in main-cart.liquid at the same time.
   * (Tax quoting is currently unconfigured, so taxCents is null and this is
   * identical to sticker-minus-deposit.)
   */
  function financedCents() {
    return (PLAN.price_cents || 0) + (state.taxCents || 0) - (PLAN.deposit_cents || 0);
  }

  /** Payment count. The LAST payment absorbs the remainder, matching the contract schedule. */
  function installmentCount() {
    var financed = financedCents();
    var reg = regularInstallmentCents();
    return financed <= 0 || reg <= 0 ? 0 : Math.ceil(financed / reg);
  }

  /**
   * Every payment date: #1 TODAY (charged at checkout — the Eastern day, as the server
   * dates it), then one every interval after it, on the same weekday. No payday to pick
   * (2026-10-13). The server holds the same rule — LAYAWAYUS-VERCEL lib/payable-day.js
   * secondPaymentDate and the CRM's contract-snapshot.ts — keep all three in step.
   */
  function scheduleDates() {
    if (!PLAN.weeks) return [];
    var n = installmentCount();
    if (n <= 0) return [];
    var today = planDay();
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    var out = [];
    for (var i = 0; i < n; i++) out.push(addDays(today, i * cad.days));
    return out;
  }

  /** The amount shown to the customer — the regular installment. */
  function perInstallmentCents() {
    return regularInstallmentCents();
  }

  /** Final payment, which carries whatever the regular installments do not. */
  function finalInstallmentCents() {
    var n = installmentCount();
    if (n <= 0) return 0;
    return financedCents() - regularInstallmentCents() * (n - 1);
  }

  function totalWithTax() {
    return (PLAN.price_cents || 0) + (state.taxCents || 0);
  }

  /**
   * The schedule moved. This used to paint a six-row recap card and a
   * "see every payment date" table into the schedule section; both are gone,
   * and the timeline — rendered by renderFinalSummary → renderTimeline — is
   * the one drawing of the plan. What is left here is the part that must
   * still run whenever the schedule moves: the totals, and the blocks below
   * that quote them.
   *
   * Every section is visible at once, so anything that moves the schedule
   * must move the authorization block and the timeline with it, or they
   * would quote a total the schedule no longer sums to. The rate and cadence
   * chips set their hidden inputs directly and fire no input event, so this —
   * not a listener — is what keeps the bottom of the form honest.
   */
  function renderSchedule() {
    renderPlanHeadline();
    updateSummaryTotals();
    renderFinalSummary();
    syncStripePlan();
  }

  function renderFinalSummary() {
    var dates = scheduleDates();
    state.shownSecond = dates.length > 1 ? isoDay(dates[1]) : null;
    var amt = perInstallmentCents();
    var el = $('final-summary');
    if (el) {
      el.innerHTML = !dates.length ? ''
        : dates.length === 1
          ? '<p><strong>' + dueTodayLabel() + ' today</strong> — paid in full when you place your order.</p>'
          : '<p><strong>' + dueTodayLabel() + ' today</strong> — your first payment, taken when you place your order. ' +
            'Then ' + money(amt) + ' ' + ((CADENCE[state.frequency] || CADENCE.weekly).label) + ' from ' +
            fmtLong(dates[1]) + ' — ' + dates.length + ' payments in all, paid off ' +
            fmtLong(dates[dates.length - 1]) + '.</p>';
    }
    // authorization block
    var name = ($('order-form-register-f-name') || {}).value + ' ' + (($('order-form-register-l-name') || {}).value || '');
    var set = function (id, v) { var n = $(id); if (n) n.textContent = v; };
    set('auth-name', name.trim() || 'the customer named above');
    set('auth-deposit', dueTodayLabel());
    set('auth-count', dates.length || '—');
    set('auth-installment', money(amt));
    set('auth-cadence', (CADENCE[state.frequency] || CADENCE.weekly).label);
    set('auth-first-date', dates.length ? fmtLong(dates[0]) : '—');
    set('auth-final-date', dates.length ? fmtLong(dates[dates.length - 1]) : '—');
    set('auth-total', money(totalWithTax()));
    set('submit-deposit', dueTodayLabel());
    // The signed authorization names today's charge, and the card panel says it plainly.
    set('sign-auth-today', dueTodayLabel());
    set('card-charge-line', 'Your first payment of ' + dueTodayLabel() + ' is charged when you place your order.');
    // prefill typed signature
    var sig = $('typed-signature');
    if (sig && !sig.value) sig.value = name.trim();
    renderTimeline();
  }

  /* ------------------------------------------------ plan-at-a-glance timeline
     The last thing the customer reads before "Start my layaway": what happens
     today, the first payments, the final one — and then the two events that
     only exist AFTER the money, the order shipping and arriving. Rendered into
     #plan-timeline at the foot of step 4.

     It reads the same scheduleDates()/installment figures the authorization
     block reads, so the two can never disagree. The ship date is an ESTIMATE:
     the ship-window setting ("14 days") counted forward from the
     final payment. When that setting is prose the code cannot count, the row
     quotes the window instead of inventing a date. Delivery gets no date at
     all — carriers, not us, decide that — so the arrival row is an event, not
     a promise.
     ---------------------------------------------------------------------- */

  /** How many payments show before the fold, and the plan length below which nothing folds. */
  var TIMELINE_HEAD_ROWS = 3;
  var TIMELINE_FOLD_FROM = 6;

  /** "$25" when there are no cents, "$12.50" when there are — the schedule table's money() shows cents always. */
  function moneyShort(cents) {
    if (cents == null || isNaN(cents)) return '—';
    if (cents % 100 !== 0) return money(cents);
    return '$' + String(cents / 100).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function addBusinessDays(d, n) {
    var x = new Date(d.getTime()), left = n;
    while (left > 0) {
      x = addDays(x, 1);
      var g = x.getDay();
      if (g !== 0 && g !== 6) left--;
    }
    return x;
  }

  /**
   * The ship window as a date, or null when the setting cannot be counted.
   * Understands "20 business days", "1–3 weeks", "3 weeks" and "10 days"; a
   * range takes its FAR end so the estimate errs late, never early.
   */
  function shipEstimate(from) {
    var w = String(PLAN.ship_window || '');
    var m;
    if ((m = /(\d+)\s*business/i.exec(w))) return addBusinessDays(from, +m[1]);
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*weeks?/i.exec(w))) return addDays(from, +m[2] * 7);
    if ((m = /(\d+)\s*weeks?/i.exec(w))) return addDays(from, +m[1] * 7);
    if ((m = /(\d+)\s*(?:–|-|to)\s*(\d+)\s*days?/i.exec(w))) return addDays(from, +m[2]);
    if ((m = /(\d+)\s*days?/i.exec(w))) return addDays(from, +m[1]);
    return null;
  }

  function cadenceNoun(count) {
    if (state.frequency === 'biweekly') return count + ' payments every 2 weeks';
    return count + ' weekly payment' + (count === 1 ? '' : 's');
  }

  var TIMELINE_ICON = {
    check: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5l3.2 3L13 4.5"/></svg>',
    truck: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1.5 3.5h8v7h-8zM9.5 6h3l2 2.5v2h-5z"/><circle cx="4.5" cy="12" r="1.3" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none"/></svg>',
    home: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 7.5L8 3l5.5 4.5M4 6.8V13h8V6.8"/><path d="M6.8 13V9.5h2.4V13"/></svg>'
  };

  function timelineRow(o) {
    return '<li class="layaway-timeline__item' + (o.mod ? ' layaway-timeline__item--' + o.mod : '') + '"' +
      (o.folded ? ' data-folded hidden' : '') + '>' +
      '<span class="layaway-timeline__node" aria-hidden="true">' + (o.icon ? TIMELINE_ICON[o.icon] : '') + '</span>' +
      '<span class="layaway-timeline__when">' + o.when + '</span>' +
      (o.note ? '<span class="layaway-timeline__note">' + o.note + '</span>' : '') +
      (o.amt != null ? '<span class="layaway-timeline__amt">' + o.amt + '</span>' : '') +
      '</li>';
  }

  function renderTimeline() {
    var box = $('plan-timeline');
    if (!box) return;
    var dates = scheduleDates();
    if (!dates.length) { box.innerHTML = ''; return; }

    var n = dates.length;
    var amt = perInstallmentCents();
    var fin = finalInstallmentCents();
    var last = dates[n - 1];
    var later = dates.slice(1);
    var m = later.length;
    var folds = m >= TIMELINE_FOLD_FROM;
    var rows = [];

    rows.push(timelineRow({
      mod: 'today', icon: 'check', when: 'Today',
      note: n === 1 ? 'Paid in full when you place your order.' : 'Your first payment, taken when you place your order.',
      amt: dueTodayLabel()
    }));

    later.forEach(function (d, i) {
      var isLast = i === m - 1;
      if (folds && i === TIMELINE_HEAD_ROWS) {
        var hiddenCount = m - 1 - TIMELINE_HEAD_ROWS;
        rows.push('<li class="layaway-timeline__item layaway-timeline__item--fold">' +
          '<span class="layaway-timeline__node" aria-hidden="true"></span>' +
          '<button type="button" class="layaway-timeline__more" data-timeline-toggle aria-expanded="false"' +
          ' data-label-more="' + hiddenCount + ' more payments of ' + moneyShort(amt) + ' — show all"' +
          ' data-label-less="Show fewer">' + hiddenCount + ' more payments of ' + moneyShort(amt) + ' — show all</button></li>');
      }
      rows.push(timelineRow({
        mod: isLast ? 'final' : null,
        icon: isLast ? 'check' : null,
        when: isLast ? fmtShortYear(d) : fmtShort(d),
        note: isLast ? 'Final payment — paid off' : (i === 0 ? 'Next payment' : null),
        amt: moneyShort(isLast ? fin : amt),
        folded: folds && i >= TIMELINE_HEAD_ROWS && !isLast
      }));
    });

    rows.push(timelineRow({
      mod: 'pace', icon: null,
      when: 'Want it sooner?',
      note: 'Raise your payment in your account any time before it\'s due.'
    }));

    // No ship-by date and no window here: the goal the customer works to is
    // the payoff, and the one date after it is the one we do not control to
    // the day. Same rule as the homepage instrument.
    rows.push(timelineRow({
      mod: 'ship', icon: 'truck',
      when: 'Ships free',
      note: 'We order it brand-new after your final payment.'
    }));
    rows.push(timelineRow({
      mod: 'home', icon: 'home',
      when: 'At your door',
      note: 'Fully paid. Nothing more to pay, ever.'
    }));

    box.innerHTML =
      '<section class="layaway-timeline" aria-label="Your plan at a glance">' +
        '<div class="layaway-timeline__head">' +
          '<span class="layaway-timeline__hero">' + dueTodayLabel() + ' today</span>' +
          '<span class="layaway-timeline__meta">' + (n === 1 ? 'Paid in full today' : cadenceNoun(n) + ' · Paid off ' + fmtDateYear(last)) + ' · Ships free</span>' +
        '</div>' +
        '<ol class="layaway-timeline__list">' + rows.join('') + '</ol>' +
      '</section>';
  }

  // Fold toggle. Bound once on the wrapper so re-renders need no re-binding.
  if ($('plan-timeline')) {
    $('plan-timeline').addEventListener('click', function (e) {
      var btn = e.target.closest('[data-timeline-toggle]');
      if (!btn) return;
      var open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      $('plan-timeline').querySelectorAll('[data-folded]').forEach(function (li) { li.hidden = !open; });
    });
  }

  function updateSummaryTotals() {
    var today = $('deposit-amount'); if (today) today.textContent = dueTodayLabel();
    var tot = $('order-total'); if (tot) tot.textContent = money(totalWithTax());
    var sd = $('submit-deposit'); if (sd) sd.textContent = dueTodayLabel();
  }

  /* ------------------------------------------------------------ tax quote */

  function maybeQuoteTax() {
    var zip = ($('order-form-register-zip') || {}).value || '';
    var st = ($('province') || {}).value || '';
    // Excluded jurisdictions never appear in the <select>, so the only gate
    // needed here is "a state is chosen". (The old st === 'DC' check compared
    // codes against full-name option values and could never match.)
    if (!/^\d{5}$/.test(zip) || !st) return;
    var amountEl = $('tax-amount');
    if (amountEl) amountEl.textContent = 'Calculating…';

    fetch(CFG.API_BASE + '/api/quote-tax', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ zip: zip, state: st, items: cartItemsPayload() })
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        // quote-tax responds with taxCents (camelCase); older builds of this
        // file read tax_cents only, which never matched, so a real quote could
        // never land even once the provider was wired. Accept both.
        var quoted = (typeof j.tax_cents === 'number') ? j.tax_cents
                   : (typeof j.taxCents === 'number') ? j.taxCents : null;
        if (quoted !== null && quoted >= 0) {
          state.taxCents = quoted;
          state.taxQuoteId = j.quote_id || j.quoteId || null;
          if (amountEl) amountEl.textContent = money(quoted);
        } else { throw new Error('bad quote'); }
        updateSummaryTotals();
        renderSchedule();
      })
      .catch(function () {
        // No quote (quote-tax is unconfigured and the server charges $0 tax).
        // The summary carries no tax row any more — a "no sales tax" line is
        // restricted in several of our states — so there is nothing to write.
        state.taxCents = null;
        if (amountEl) amountEl.textContent = '';
        updateSummaryTotals();
      });
  }

  /* ----------------------------------------------------------- cart items */

  function cartItemsPayload() {
    var cart = window.cartData || { items: [] };
    var totals = window.itemTotalValues || {};
    var amt = perInstallmentCents();
    return (cart.items || []).map(function (it) {
      var extra = totals[it.id] || {};
      var totalValue = it.final_line_price != null ? it.final_line_price : extra.total_value_cents;
      return {
        variant_id: it.variant_id || it.id,
        product_id: it.product_id,
        title: it.product_title || it.title,
        handle: extra.handle || it.handle,
        image: it.image,
        price: amt,
        total_value: totalValue,
        weeks: PLAN.weeks || 0,
        quantity: it.quantity,
        tags: extra.tags || [],
        properties: it.properties || {},
        replacement_transfer: false,
        promo_application: null
      };
    });
  }

  /* ------------------------------------------------------------ persisted */

  var PERSIST = ['order-form-register-email', 'order-form-register-phone', 'order-form-register-f-name',
    'order-form-register-l-name', 'dob', 'order-form-register-street-address',
    'order-form-register-street-address-2', 'order-form-register-city', 'province',
    'order-form-register-zip', 'order-form-weekly-rate', 'order-form-pay-frequency',
    'gift-f-name', 'gift-l-name', 'gift-phone', 'gift-street-address', 'gift-street-address-2',
    'gift-city', 'gift-province', 'gift-zip', 'gift-message'];

  var persistTimer = null;
  function persist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(function () {
      try {
        var data = {};
        PERSIST.forEach(function (id) { var el = $(id); if (el) data[id] = el.value; });
        data.__step = state.step;
        data.__gift = giftOn();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) { /* storage full or blocked */ }
    }, 400);
  }

  function restore() {
    var data;
    try { data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (e) { return; }
    PERSIST.forEach(function (id) {
      if (!data[id]) return;
      var el = $(id);
      if (!el || el.value) return;
      if (id === 'province' || id === 'gift-province') {
        var savedState = String(data[id]).trim().toLowerCase();
        var match = Array.prototype.find.call(el.options, function (option) {
          return option.value && (option.value.toLowerCase() === savedState ||
            (option.getAttribute('data-code') || '').toLowerCase() === savedState);
        });
        el.value = match ? match.value : '';
        if (!match) el.dataset.unsupportedState = data[id];
      } else el.value = data[id];
    });
    // The rate is restored only if it is still on offer; a retired one falls through
    // to the default rather than reaching create-order and being refused at the last
    // step. The chips are rendered after restore(), so setting state is enough.
    var savedRate = parseInt(data['order-form-weekly-rate'], 10);
    if (RATES.indexOf(savedRate) >= 0) state.weeklyRateCents = savedRate;

    var freq = data['order-form-pay-frequency'];
    // A cadence no longer sold here (every 4 weeks, saved before 2026-10-05) falls back
    // to weekly rather than riding into the order and being refused at the last step.
    if (freq && !CADENCE[freq]) {
      freq = 'weekly';
      var freqInput = $('order-form-pay-frequency'); if (freqInput) freqInput.value = 'weekly';
    }
    if (freq) {
      state.frequency = freq;
      var row = $('frequency-selector');
      if (row) row.querySelectorAll('.layaway-chip').forEach(function (c) {
        c.classList.toggle('is-selected', c.dataset.value === freq);
      });
    }
  }
  function clearPersisted() { try { localStorage.removeItem(STORAGE_KEY); } catch (e) {} }

  /* ---------------------------------------------------------- save-cart */

  // Capture must never prevent a customer from finishing the order.
  var checkoutCapture = {id:null,schedule:function(){},flush:function(){return Promise.resolve();},stop:function(){}};
  if (window.LayawayCheckoutCapture && !new URLSearchParams(location.search).has('resume_checkout')) {
    checkoutCapture = window.LayawayCheckoutCapture.create({
    endpoint: window.LayawayCheckoutAPI + '/capture', site: CFG.SITE,
    token: (window.cartData || {}).token,
    onToken: function (token) { if (window.cartData) window.cartData.token = token; },
    snapshot: function () {
      return {
        token: (window.cartData || {}).token, site: CFG.SITE,
        customer: {
          email: ($('order-form-register-email') || {}).value || '',
          phone: ($('order-form-register-phone') || {}).value || '',
          first_name: ($('order-form-register-f-name') || {}).value || '',
          last_name: ($('order-form-register-l-name') || {}).value || ''
        },
        items: cartItemsPayload().map(function (item) {
          return {variant_id:item.variant_id,quantity:item.quantity,total_value:item.total_value,
            title:item.title,handle:item.handle,image:item.image};
        }), step: state.step, attribution: attribution()
      };
    }
  });
  }
  form.addEventListener('input', checkoutCapture.schedule);
  form.addEventListener('change', checkoutCapture.schedule);
  form.addEventListener('focusout', function () { checkoutCapture.flush(); });
  checkoutCapture.schedule();

  /* ------------------------------------------------------------- attribution */

  function attribution() {
    try { return JSON.parse(localStorage.getItem('ld_attribution') || '{}'); } catch (e) { return {}; }
  }
  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return m ? m[1] : '';
  }

  /* ------------------------------------------------------------ Meta events */

  function metaTrack(event, payload, options) {
    // The event id MUST travel in the fourth argument. Inside the payload it is
    // just custom data: Meta stamps its own id on the browser event, the pair
    // never dedupes against the server's Conversions API event, and sales
    // counted twice in Ads Manager (found 2026-09-24).
    try {
      if (!window.fbq) return;
      if (options) window.fbq('track', event, payload || {}, options);
      else window.fbq('track', event, payload || {});
    } catch (e) {}
  }
  /**
   * The Purchase data as create-order's Conversions API copy sends it: value,
   * currency, the order reference and the catalogue's variant ids. Used only
   * when the server's reply does not carry its own copy (metaPurchase).
   */
  function purchaseData(payload, body) {
    var items = ((payload && payload.items) || []).filter(function (it) { return it && it.variant_id; });
    var contents = items.map(function (it) {
      var qty = it.quantity || 1;
      return { id: String(it.variant_id), quantity: qty, item_price: Math.round((it.total_value || 0) / qty) / 100 };
    });
    var data = {
      value: totalWithTax() / 100,
      currency: CFG.CURRENCY || 'USD',
      content_type: 'product',
      content_ids: contents.map(function (c) { return c.id; }),
      contents: contents,
      num_items: contents.reduce(function (n, c) { return n + c.quantity; }, 0)
    };
    var ref = body && (body.order_reference || body.orderReference);
    if (ref) data.order_id = ref;
    return data;
  }
  metaTrack('InitiateCheckout', {
    value: (PLAN.price_cents || 0) / 100,
    currency: 'USD',
    num_items: (window.cartData && window.cartData.item_count) || 0
  });
  var firedAddPayment = false;
  form.addEventListener('focusin', function () {
    if (firedAddPayment) return;
    firedAddPayment = true;
    metaTrack('AddPaymentInfo', { value: (PLAN.price_cents || 0) / 100, currency: 'USD' });
  });

  /* ------------------------------------------------------------- Stripe */

  /*
   * WHAT A BLOCKED BROWSER LOOKS LIKE. An ad or tracking blocker (or Safari's own
   * protection) refuses js.stripe.com and r.stripe.com; the refused beacons then exhaust
   * the browser's 64KB keepalive budget, and the real confirm call dies with them. Stripe
   * reports a connection it could not make as `api_connection_error`, but a fetch the
   * browser refused outright never reaches Stripe's error shape at all and surfaces as a
   * TypeError whose wording differs per engine. To the shopper these are one thing.
   *
   * A card_error is never a connection problem, and is excluded first so that a decline
   * whose text happens to contain one of these words cannot be read as a blocker.
   */
  var BLOCKED_MESSAGE_RE = /load failed|failed to fetch|network\s*(?:error|request|connection)|connection was lost|err_blocked|blocked by client/i;
  function isBlockedConnection(err) {
    if (!err) return false;
    if (err.type === 'card_error' || err.type === 'validation_error') return false;
    if (err.type === 'api_connection_error' || err.code === 'api_connection_error') return true;
    return BLOCKED_MESSAGE_RE.test(String(err.message || ''));
  }

  /* Naming Stripe is deliberate: it is already named beside the card field, and it is the
     exact thing the shopper has to allow. The second line is used where the request that
     failed was to our own CRM, which we cannot blame on Stripe. */
  var BLOCKED_REMEDY = 'This is usually an ad or tracking blocker \u2014 turn it off for this page, or try another browser.';
  var BLOCKED_STRIPE = 'We couldn\'t reach Stripe, our payment processor. ' + BLOCKED_REMEDY;
  var BLOCKED_SYSTEM = 'We couldn\'t reach our payment system. ' + BLOCKED_REMEDY;
  var CARD_GENERIC = 'We couldn\'t take that payment. Please check your card details and try again.';

  function withStripe(cb) {
    if (window.Stripe) return cb();
    var deadline = Date.now() + 10000;
    var t = setInterval(function () {
      if (form.layawayAbandoned) { clearInterval(t); return; }
      if (window.Stripe) { clearInterval(t); cb(); }
      else if (Date.now() >= deadline) {
        clearInterval(t);
        state.stripeMounted = false;
        state.stripeRetryRequired = true;
        showCardError(BLOCKED_STRIPE);
        if ($('stripe-retry')) $('stripe-retry').hidden = false;
      }
    }, 100);
  }

  function showCardError(msg) {
    var box = $('stripe-card-errors');
    if (box) box.textContent = msg || '';
  }

  /**
   * The card form, mounted before any PaymentIntent exists ("deferred"): mode 'payment'
   * for the first payment's amount, saving the card for the rest of the plan
   * (setupFutureUsage must match what checkout-charge creates). The publishable key comes
   * from the same server that will confirm the charge, so the two cannot be from
   * different Stripe accounts. No intent is created until the customer presses Pay.
   */
  function mountStripe() {
    if (state.stripeMounted || state.stripeRetryRequired) return;
    var email = ($('order-form-register-email') || {}).value || '';
    if (!RE_EMAIL.test(email)) return;
    state.stripeMounted = true;
    showCardError('');
    if ($('stripe-retry')) $('stripe-retry').hidden = true;

    withStripe(function () {
      fetch(CFG.CARD_API_BASE + '/api/stripe/checkout-charge', { method: 'GET' })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (j) {
          if (form.layawayAbandoned) return;
          state.publishableKey = j.publishableKey || CFG.STRIPE_PK;
          try { sessionStorage.setItem(PK_KEY, state.publishableKey); } catch (e) {}
          state.stripe = window.Stripe(state.publishableKey);
          state.elements = state.stripe.elements({
            mode: 'payment',
            amount: firstPaymentCents(),
            currency: 'usd',
            setupFutureUsage: 'off_session',
            // Card type only, which still takes Apple Pay, Google Pay and Link: each arrives as
            // a card (a Link bank payment as brand 'link', funding 'unknown'), so the server
            // sees every real card's funding type and turns a prepaid card away before
            // charging it. Link as its OWN type would hide it (Adrian kept it in the card
            // form, 2026-10-08). Must match checkout-charge's intent.
            paymentMethodTypes: ['card'],
            appearance: APPEARANCE
          });
          var paymentOptions = {
            // Every way to pay on show at once, phone wallets first: one tap on a phone,
            // and no card number to type. Not one charge 21 Sep–8 Oct came from either.
            layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: true },
            paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
            wallets: { applePay: 'auto', googlePay: 'auto' },
            defaultValues: { billingDetails: { email: email, address: { country: 'US' } } }
          };
          // The Apple Pay plan; syncStripePlan keeps it on the plan after.
          var applePay = applePayPlan();
          if (applePay.recurringPaymentRequest) paymentOptions.applePay = applePay;
          state.paymentElement = state.elements.create('payment', paymentOptions);
          state.paymentElement.mount('#stripe-payment-element');
          if ($('pay-waiting')) $('pay-waiting').hidden = true;
          // A form Stripe could not load (a content blocker, a VPN's tracker blocking) must
          // never leave a blank section: say why and offer the retry.
          if (typeof state.paymentElement.on === 'function') {
            state.paymentElement.on('loaderror', function () {
              // Retry mounts a fresh form into the same box: take the failed one out first.
              try { if (state.paymentElement && state.paymentElement.destroy) state.paymentElement.destroy(); } catch (e) {}
              state.paymentElement = null;
              state.stripeMounted = false;
              state.stripeRetryRequired = true;
              if ($('stripe-retry')) $('stripe-retry').hidden = false;
              showCardError(BLOCKED_STRIPE);
            });
          }
          if (typeof state.paymentElement.on === 'function') state.paymentElement.on('change', onPaymentMethodChange);
          detectWallets();
          state.bankLink = j.bankLink === true || BANK_TEST;
          if (state.bankLink && $('pay-choice')) $('pay-choice').hidden = false;
        })
        .catch(function (reason) {
          state.stripeMounted = false; // allow retry
          state.stripeRetryRequired = true;
          if ($('stripe-retry')) $('stripe-retry').hidden = false;
          showCardError(isBlockedConnection(reason)
            ? BLOCKED_SYSTEM
            : 'We couldn\'t start secure payment. Please try again in a moment.');
        });
    });
  }

  /*
   * Apple Pay for a plan: ask for a merchant token. It belongs to the customer and this store
   * rather than to one iPhone, so the later payments keep working after a phone change; without
   * it Apple Pay hands over a device token, which can stop working when the phone is replaced.
   * Payment #1 is the charge itself, so this describes #2 onward. {} until the plan has dates.
   */
  var PORTAL_URL = 'https://my.laybyusa.com';
  function applePayPlan() {
    var dates = scheduleDates();
    var amount = perInstallmentCents();
    if (dates.length < 2 || !amount) return {};
    var cad = CADENCE[state.frequency] || CADENCE.weekly;
    return {
      recurringPaymentRequest: {
        paymentDescription: 'Your layaway plan payments',
        managementURL: PORTAL_URL,
        regularBilling: {
          amount: amount,
          label: state.frequency === 'biweekly' ? 'Payment every 2 weeks' : 'Weekly payment',
          recurringPaymentStartDate: dates[1],
          recurringPaymentEndDate: dates[dates.length - 1],
          recurringPaymentIntervalUnit: 'day',
          recurringPaymentIntervalCount: cad.days
        }
      }
    };
  }

  /** Keep Stripe on the plan as it changes (rate, cadence, cart): today's amount in both forms, and the Apple Pay plan. */
  function syncStripePlan() {
    [state.elements, state.bankElements].forEach(function (els) {
      if (els && els.update) { try { els.update({ amount: firstPaymentCents() }); } catch (e) {} }
    });
    var pe = state.paymentElement;
    if (pe && pe.update) {
      try { pe.update({ applePay: applePayPlan() }); } catch (e) {}
    }
  }

  /* ------------------------------------------------ try another way to pay */

  /*
   * Which phone wallets this device has, by Stripe's own check (the device and the registered
   * domain — what the Payment Element itself needs to show them). The "try another way to
   * pay" box names only these, so nobody is told to use an Apple Pay they cannot see.
   */
  function detectWallets() {
    try {
      if (!state.stripe || typeof state.stripe.paymentRequest !== 'function') return;
      state.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: { label: 'First payment', amount: firstPaymentCents() || 100 }
      }).canMakePayment().then(function (r) {
        state.wallets = { applePay: !!(r && r.applePay), googlePay: !!(r && r.googlePay) };
      }).catch(function () {});
    } catch (e) {}
  }

  /** How a ConfirmationToken pays: a card, a phone wallet, or Link (a saved card, or a bank account). */
  function methodOf(preview) {
    var card = preview && preview.card;
    var wallet = card && card.wallet && card.wallet.type;
    if (wallet === 'apple_pay' || wallet === 'google_pay') return wallet;
    // Link's non-card payments (a bank account) arrive as a card with brand 'link', last4 0000.
    if (wallet === 'link') return card.brand === 'link' ? 'link_other' : 'link_card';
    // The bank choice: Link as its own payment method.
    if (preview && preview.type === 'link') return 'link';
    return 'card';
  }
  /** The Payment Element's own name for where a method lives: wallets have their own row, the rest are in Card. */
  function elementTypeOf(method) {
    if (method === 'apple_pay' || method === 'google_pay' || method === 'link') return method;
    return 'card';
  }

  var FORK_WALLETS = [
    { key: 'applePay', method: 'apple_pay', label: 'Apple Pay' },
    { key: 'googlePay', method: 'google_pay', label: 'Google Pay' }
  ];

  /*
   * A decline is not the end of the checkout: name the other ways to pay, which the accordion
   * already lists. The card form stays open, so a mistyped security code can be fixed in
   * place. The next press confirms the SAME intent, so it can never charge twice. "Nothing
   * was charged" only after a definite answer — while an attempt is in doubt (see
   * setChargeUnknown) the box says nothing about it.
   */
  function showPayFork() {
    var box = $('pay-fork');
    var list = $('pay-fork-list');
    if (!box || !list) return;
    var items;
    if (state.payDoor === 'bank') {
      // The bank was declined: the card choice, with Apple Pay and Google Pay in it.
      items = ['<li><strong>A card, Apple Pay or Google Pay</strong><br>'
        + '<button type="button" id="pay-fork-card" class="btn btn--secondary">Pay by card instead</button></li>'];
    } else {
      items = FORK_WALLETS.filter(function (w) {
        return state.wallets[w.key] && state.lastMethod !== w.method;
      }).map(function (w) {
        return '<li><strong>' + w.label + '</strong> — choose it in the list above.</li>';
      });
      items.push('<li><strong>A different debit or credit card</strong><br>'
        + '<button type="button" id="pay-fork-new-card" class="btn btn--secondary">Enter a different card</button></li>');
      if (state.bankLink) {
        items.push('<li><strong>Your bank account</strong> — instantly, with Link<br>'
          + '<button type="button" id="pay-fork-bank" class="btn btn--secondary">Pay from my bank instead</button></li>');
      }
    }
    list.innerHTML = items.join('');
    var note = $('pay-fork-note');
    if (note) note.textContent = state.chargeUnknown ? '' : 'Nothing was charged.';
    box.hidden = false;
  }
  function hidePayFork() {
    var box = $('pay-fork');
    if (box) box.hidden = true;
  }
  /*
   * Picking another way to pay is the answer to the box. The form collapsing itself is not a
   * choice, and neither is picking the method that was just declined.
   */
  function onPaymentMethodChange(e) {
    if (!e || e.collapsed || !e.value || !e.value.type) return;
    if (e.value.type !== elementTypeOf(state.lastMethod)) hidePayFork();
  }
  if ($('pay-fork')) $('pay-fork').addEventListener('click', function (e) {
    var hit = function (sel) { return e.target && e.target.closest && e.target.closest(sel); };
    if (hit('#pay-fork-bank')) { setPayDoor('bank'); return; }
    if (hit('#pay-fork-card')) { setPayDoor('card'); return; }
    if (!hit('#pay-fork-new-card')) return;
    var pe = state.paymentElement;
    try { if (pe && pe.clear) pe.clear(); } catch (err) {}
    try { if (pe && pe.focus) pe.focus(); } catch (err) {}
  });

  /* ------------------------------------------------------- card or bank */

  /*
   * Card or bank, both instant (Adrian 2026-10-08). The bank is Stripe's Instant Bank
   * Payments through Link as its own payment method, which Stripe shows only beside 'card' —
   * so it is a second payment form, made the first time Bank is picked. The card form is
   * never touched: Apple Pay, Google Pay, Link inside the card form and every card check
   * (the prepaid refusal above all) stay exactly as they are.
   */
  function setPayDoor(door) {
    if (door !== 'card' && door !== 'bank') return;
    if (door === 'bank' && !state.bankLink) return;
    if (door === 'bank' && !mountBankForm()) { bankUnavailable(); return; }
    state.payDoor = door;
    Array.prototype.forEach.call(document.querySelectorAll('#pay-choice [data-pay]'), function (b) {
      b.setAttribute('aria-checked', b.getAttribute('data-pay') === door ? 'true' : 'false');
    });
    if ($('stripe-payment-element')) $('stripe-payment-element').hidden = door !== 'card';
    if ($('stripe-bank-element')) $('stripe-bank-element').hidden = door !== 'bank';
    hidePayFork();
    showCardError('');
  }
  function mountBankForm() {
    if (state.bankElements) return true;
    if (!state.stripe || !$('stripe-bank-element')) return false;
    var email = ($('order-form-register-email') || {}).value || '';
    // Shown before Stripe mounts into it (a hidden box can size the form wrongly); the card
    // form stays until this succeeds, so a failure never leaves the page without one.
    $('stripe-bank-element').hidden = false;
    try {
      state.bankElements = state.stripe.elements({
        mode: 'payment',
        amount: firstPaymentCents(),
        currency: 'usd',
        setupFutureUsage: 'off_session',
        // Link as its own payment method. Must match checkout-charge's intent for Link.
        paymentMethodTypes: ['card', 'link'],
        appearance: APPEARANCE
      });
      state.bankElement = state.bankElements.create('payment', {
        layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: true },
        paymentMethodOrder: ['link', 'card'],
        // The phone wallets live in the card form.
        wallets: { applePay: 'never', googlePay: 'never' },
        defaultValues: { billingDetails: { email: email, address: { country: 'US' } } }
      });
      state.bankElement.mount('#stripe-bank-element');
      if (typeof state.bankElement.on === 'function') state.bankElement.on('change', onBankFormChange);
    } catch (e) {
      state.bankElements = null;
      state.bankElement = null;
      $('stripe-bank-element').hidden = true;
      return false;
    }
    return true;
  }
  /*
   * Stripe offers a bank per shopper (US customers, and only when its own check passes). The
   * bank form's first answer says which: Link first means a bank is offered. Card first means
   * none is — a "Bank account" choice that opens card fields would mislead, so it goes away
   * with a plain note. Cards picked inside the bank form are paid in the Card choice, where
   * Apple Pay, Google Pay and every card check are.
   */
  var BANK_UNAVAILABLE = 'Paying straight from your bank isn\'t available for this order, so please pay by card.';
  function onBankFormChange(e) {
    if (!e || e.collapsed || !e.value || !e.value.type) return;
    onPaymentMethodChange(e);
    if (e.value.type === 'link') { state.bankOffered = true; return; }
    if (state.bankOffered === true) { setPayDoor('card'); return; }
    bankUnavailable();
  }
  /** No bank for this shopper: back to the card form, the Bank choice gone, and why. */
  function bankUnavailable() {
    withdrawBankChoice();
    showCardError(BANK_UNAVAILABLE);
  }
  function withdrawBankChoice() {
    setPayDoor('card');
    state.bankLink = false;
    if ($('pay-choice')) $('pay-choice').hidden = true;
  }
  /** The payment form on show: the bank's once Bank is picked, otherwise the card's. */
  function activeElements() {
    return state.payDoor === 'bank' && state.bankElements ? state.bankElements : state.elements;
  }
  if ($('pay-choice')) $('pay-choice').addEventListener('click', function (e) {
    var opt = e.target && e.target.closest && e.target.closest('[data-pay]');
    if (opt) setPayDoor(opt.getAttribute('data-pay'));
  });

  if ($('stripe-retry')) $('stripe-retry').addEventListener('click', function () {
    if (state.stripeMounted) return;
    if (new URLSearchParams(location.search).get('payment_return') === '1') { location.reload(); return; }
    state.stripeRetryRequired = false;
    if (!window.Stripe) {
      var old = document.getElementById('stripe-js-sdk');
      if (old) {
        var script = document.createElement('script');
        script.id = old.id;
        script.src = old.src;
        old.replaceWith(script);
      }
    }
    mountStripe();
  });

  /*
   * What the customer reads when the first payment is declined. Nothing was charged on a
   * decline, and the form stays as it is: they can fix a field or use another card and
   * press Pay again — the same checkout, never a second charge. Stolen / lost / fraud
   * codes get the generic line on purpose (never echo them back).
   */
  var DECLINED = 'Your bank didn\'t approve this card. Try again, or use a different card.';
  var DECLINE_COPY = {
    card_declined: DECLINED,
    generic_decline: DECLINED,
    do_not_honor: DECLINED,
    lost_card: DECLINED,
    stolen_card: DECLINED,
    fraudulent: DECLINED,
    insufficient_funds: 'There isn\'t enough available on this card right now. Try another card.',
    transaction_not_allowed: 'Your bank won\'t allow this card for this payment. Please try another card.',
    try_again_later: 'Your bank couldn\'t approve this right now. Try again in a few minutes, or use another card.',
    expired_card: 'That card looks expired. Try a different card.',
    incorrect_cvc: 'The security code didn\'t match. Check it and try again.',
    incorrect_number: 'That card number doesn\'t look right. Please check it.',
    invalid_number: 'That card number doesn\'t look right. Please check it.',
    prepaid_card: 'Prepaid cards can\'t be used for a payment plan. Please use a debit or credit card from your bank.',
    card_only: 'Please pay with a debit or credit card.',
    authentication_required: 'Your bank asked to confirm it\'s you and that didn\'t finish. Try again, and look out for a message from your banking app.',
    payment_intent_authentication_failure: 'Your bank asked to confirm it\'s you and that didn\'t finish. Try again, and look out for a message from your banking app.',
    processing_error: 'Something went wrong on the bank\'s end. Please try again.'
  };
  /*
   * A Link payment from a bank account is not a card, so "didn't approve this card" misleads:
   * three customers 5–7 Oct had a Link bank payment declined and were told it was their card.
   * Worded for any non-card Link source, not only a bank.
   */
  var LINK_DECLINED = 'Your Link payment wasn\'t approved. Pay with a debit or credit card instead.';
  function declineCopy(code) {
    var link = state.lastMethod === 'link_other' || state.lastMethod === 'link';
    if (link && code !== 'processing_error' && code !== 'try_again_later') return LINK_DECLINED;
    return DECLINE_COPY[code] || DECLINED;
  }
  /** A failed bank check that is a definite "not paid" — never a lost connection, after which the check may have passed. */
  function definitelyNotPaid(err) {
    return !!err && (err.type === 'card_error' || err.code === 'payment_intent_authentication_failure'
      || !!(err.payment_intent && err.payment_intent.status === 'requires_payment_method'));
  }
  /*
   * An ALLOWLIST, not a fallthrough. Only Stripe's `card_error` and `validation_error`
   * messages are written for a shopper to read; every other type carries infrastructure
   * wording that reads as OUR outage once it lands in our error box. On 2026-09-21 a
   * blocked browser put "We are experiencing issues connecting to our payments provider.
   * Please check your internet connection." above a checkout that was working perfectly,
   * telling the customer to fix a connection that was fine. Anything we cannot vouch for
   * becomes our own generic line instead.
   */
  function friendlyCardError(err) {
    if (!err) return CARD_GENERIC;
    if (err.decline_code && DECLINE_COPY[err.decline_code]) return DECLINE_COPY[err.decline_code];
    if (err.code && DECLINE_COPY[err.code]) return DECLINE_COPY[err.code];
    if (isBlockedConnection(err)) return BLOCKED_STRIPE;
    if (err.type === 'card_error' || err.type === 'validation_error') return err.message || CARD_GENERIC;
    return CARD_GENERIC;
  }

  /* ---------------------------------------------------------- payload */

  function buildPayload() {
    var payload = buildOrderPayload();
    payload.formDetails = payload.formDetails || {};
    payload.formDetails.checkout_id = checkoutCapture.getId ? checkoutCapture.getId() : checkoutCapture.id;
    payload.formDetails.cart_token = (window.cartData || {}).token || '';
    return payload;
  }
  function buildOrderPayload() {
    var dates = scheduleDates();
    var v = function (id) { var el = $(id); return el ? (el.value || '').trim() : ''; };
    var checked = function (id) { var el = $(id); return !!(el && el.checked); };
    // The signature is the recurring card authorization (agreement clause 4A): a typed
    // name and real ink, the same two things validateField demands before submit.
    var signed = v('typed-signature').length >= 3 && !!(sigPad && sigPad.hasInk());
    return {
      site: CFG.SITE,
      customer: {
        first_name: v('order-form-register-f-name'),
        last_name: v('order-form-register-l-name'),
        email: v('order-form-register-email'),
        phone: v('order-form-register-phone'),
        address: v('order-form-register-street-address'),
        address2: v('order-form-register-street-address-2'),
        city: v('order-form-register-city'),
        state: v('province'),
        postcode: v('order-form-register-zip')
      },
      items: cartItemsPayload(),
      // `customer` is always the signer. `delivery` is where the box goes,
      // and is only sent when it is somebody else's address; create-order
      // treats its absence as "deliver to the signer", which is what every
      // order before the gift did.
      delivery: giftOn() ? {
        first_name: v('gift-f-name'),
        last_name: v('gift-l-name'),
        phone: v('gift-phone'),
        address: v('gift-street-address'),
        address2: v('gift-street-address-2'),
        city: v('gift-city'),
        state: v('gift-province'),
        postcode: v('gift-zip')
      } : null,
      gift: giftOn() ? {
        is_gift: true,
        recipient_first_name: v('gift-f-name'),
        recipient_last_name: v('gift-l-name'),
        recipient_phone: v('gift-phone'),
        message: v('gift-message')
      } : null,
      formDetails: {
        date_of_birth: v('dob'),
        gender: '',
        // Payment #2 as the customer was shown it. The server keeps it when it is this page's
        // day + cadence (today or yesterday, Eastern), else dates the plan itself.
        next_pay_date: state.shownSecond || '',
        pay_frequency: v('order-form-pay-frequency') || state.frequency,
        // The chosen weekly rate. create-order normalises it against its own list and
        // then runs verifyClientPlan, so this cannot name a payment we do not sell —
        // a disagreement is refused rather than silently corrected.
        weekly_rate_cents: state.weeklyRateCents,
        promo_code: v('promo-code'),
        affiliate_referral_code: (function () {
          try { return localStorage.getItem('layaway_affiliate_code') || ''; } catch (e) { return ''; }
        })(),
        attribution: attribution(),
        // One box carries the agreement, its cancellation policy and the E-SIGN consent;
        // the signature carries the card authorization. create-order still refuses the
        // order unless all four are true (lib/agreement.js) and the contract records all
        // four, so what is evidenced is exactly what was evidenced with four boxes.
        agreed_to_electronic_records: checked('agreed_to_terms'),
        agreed_to_terms: checked('agreed_to_terms'),
        agreed_to_charges: false,
        agreed_to_cancellation: checked('agreed_to_terms'),
        agreed_to_payment_info: signed,
        confirmed_affordability: false,
        subscribe_to_mailing: checked('order-subscribe-to-mailing-list'),
        sms_consent: checked('sms-consent'),
        is_bundle: (window.cartData && window.cartData.item_count > 1) || false,
        bundle_item_count: (window.cartData && window.cartData.item_count) || 0,
        payment_method: 'card',
        // The $0 card check is gone; the first payment's PaymentIntent replaces it.
        stripe_setup_intent_id: '',
        stripe_payment_intent_id: state.paidIntentId || '',
        signature: v('typed-signature'),
        // Drawn signature as a PNG dataURL. The NZ/AU webhook already accepts
        // 'Electronic Signature' dataURLs and uploads them to Blob, so this
        // matches the existing backend contract rather than inventing one.
        signature_image: (sigPad && sigPad.dataUrl()) || '',
        signature_signed_at: new Date().toISOString(),
        submitted_at: new Date().toISOString(),
        // Kept for the backend contract (0): the first payment is payment #1 of the plan,
        // not a deposit on top of it.
        deposit_cents: PLAN.deposit_cents,
        installment_cents: perInstallmentCents(),
        installment_count: dates.length,
        final_payment_date: dates.length ? fmtSlash(dates[dates.length - 1]) : '',
        tax_cents: state.taxCents,
        tax_quote_id: state.taxQuoteId,
        estimated_delivery_window: PLAN.ship_window || ''
      },
      weekly_payment_cents: perInstallmentCents(),
      fbp: cookie('_fbp'),
      fbc: cookie('_fbc'),
      client_ip: '',
      // One id for the whole checkout: the server keys this checkout's one PaymentIntent
      // on it, so pressing Pay again after a decline confirms the same intent.
      event_id: state.checkoutEventId
    };
  }

  function checkoutSession() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(CHECKOUT_KEY) || 'null');
      if (saved && /^lw-[A-Za-z0-9-]{6,60}$/.test(saved.eventId || '')) {
        state.checkoutEventId = saved.eventId;
        state.paidIntentId = /^pi_[A-Za-z0-9]+$/.test(saved.paidIntentId || '') ? saved.paidIntentId : null;
        state.chargeUnknown = saved.chargeUnknown === true;
        return;
      }
    } catch (e) {}
    state.checkoutEventId = 'lw-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    saveCheckoutSession();
  }
  function saveCheckoutSession() {
    try {
      sessionStorage.setItem(CHECKOUT_KEY, JSON.stringify({
        eventId: state.checkoutEventId, paidIntentId: state.paidIntentId, chargeUnknown: state.chargeUnknown === true
      }));
    } catch (e) {}
  }
  function clearCheckoutSession() { try { sessionStorage.removeItem(CHECKOUT_KEY); } catch (e) {} }

  /** The signer's details for the card's billing address — Stripe's address check reads them. */
  function billingDetails(payload) {
    var c = payload.customer || {};
    var sel = $('province');
    var opt = sel && sel.options ? sel.options[sel.selectedIndex] : null;
    return {
      name: [c.first_name, c.last_name].filter(Boolean).join(' '),
      email: c.email,
      phone: c.phone,
      address: {
        line1: c.address, line2: c.address2 || '', city: c.city,
        state: (opt && opt.getAttribute('data-code')) || c.state,
        postal_code: c.postcode, country: 'US'
      }
    };
  }


  /* --------------------------------------------------------- signature pad
     Hand-drawn signature, no CDN dependency. Pointer Events cover mouse,
     touch and stylus in one path. The canvas is sized to its CSS box * DPR so
     strokes are sharp on retina, and re-sized on rotate/resize WITHOUT losing
     existing ink (we replay the stroke list rather than scaling a bitmap).
     Exported as a trimmed PNG dataURL into order[signature_image]. */

  var sigPad = (function () {
    var canvas = $('signature-pad');
    if (!canvas) return null;

    var ctx = canvas.getContext('2d');
    var frame = canvas.parentNode;
    var hidden = $('signature-image');
    var statusEl = $('sigpad-status');
    var strokes = [];       // [[{x,y},...], ...] in CSS pixels
    var current = null;
    var drawing = false;

    function dpr() { return window.devicePixelRatio || 1; }

    function resize() {
      var r = canvas.getBoundingClientRect();
      if (!r.width) return;
      canvas.width = Math.round(r.width * dpr());
      canvas.height = Math.round(r.height * dpr());
      redraw();
    }

    function redraw() {
      var d = dpr();
      ctx.setTransform(d, 0, 0, d, 0, 0);
      ctx.clearRect(0, 0, canvas.width / d, canvas.height / d);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#2b1f1a';
      strokes.forEach(function (pts) {
        if (pts.length < 2) {
          // a single tap still deserves a visible dot
          if (pts.length === 1) {
            ctx.beginPath();
            ctx.arc(pts[0].x, pts[0].y, 1.2, 0, Math.PI * 2);
            ctx.fillStyle = '#2b1f1a';
            ctx.fill();
          }
          return;
        }
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        ctx.stroke();
      });
    }

    function pos(e) {
      var r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }

    function start(e) {
      drawing = true;
      current = [pos(e)];
      strokes.push(current);
      if (canvas.setPointerCapture && e.pointerId != null) {
        try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      }
      e.preventDefault();
    }

    function move(e) {
      if (!drawing) return;
      current.push(pos(e));
      redraw();
      e.preventDefault();
    }

    function end() {
      if (!drawing) return;
      drawing = false;
      current = null;
      commit();
    }

    function hasInk() {
      // a stray tap is not a signature — require real travel
      var pts = 0;
      for (var i = 0; i < strokes.length; i++) pts += strokes[i].length;
      return strokes.length > 0 && pts >= 8;
    }

    function commit() {
      frame.classList.toggle('has-ink', hasInk());
      if (hidden) hidden.value = hasInk() ? canvas.toDataURL('image/png') : '';
      if (statusEl) {
        statusEl.textContent = hasInk() ? 'Signature captured' : '';
        statusEl.classList.toggle('is-signed', hasInk());
      }
      if (hasInk()) setError('sigpad-error', '');
    }

    canvas.addEventListener('pointerdown', start);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('pointerleave', end);

    var clearBtn = $('sigpad-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        strokes = [];
        redraw();
        commit();
      });
    }

    window.addEventListener('resize', resize);
    resize();

    return {
      hasInk: hasInk,
      dataUrl: function () { return hasInk() ? canvas.toDataURL('image/png') : ''; },
      clear: function () { strokes = []; redraw(); commit(); },
      resize: resize
    };
  })();

  /* ------------------------------------------------------------- submit */

  function showFormErrors(messages, title) {
    var modal = $('form-errors-modal');
    var list = $('form-errors-list');
    var head = $('form-errors-title');
    // The title is settable so a reassuring notice is not shown under
    // "Please fix these to continue" — which is what exit-intent used to do.
    if (head) head.textContent = title || 'Please fix these to continue';
    if (modal) modal.setAttribute('role', title ? 'dialog' : 'alertdialog');
    if (list) list.innerHTML = messages.map(function (m) { return '<li>' + m + '</li>'; }).join('');
    if (modal) modal.hidden = false;
  }
  function closeFormErrors() {
    var m = $('form-errors-modal');
    if (m) m.hidden = true;
  }
  // querySelectorAll, not querySelector: the overlay AND the OK button both
  // carry data-close-errors, and the overlay comes first in the markup.
  Array.prototype.forEach.call(
    document.querySelectorAll('#form-errors-modal [data-close-errors]'),
    function (el) { el.addEventListener('click', closeFormErrors); }
  );
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeFormErrors();
  });

  function setSubmitting(on, label) {
    state.submitting = on;
    var btn = $('btn-submit');
    if (!btn) return;
    applySubmitBlocks();
    var txt = btn.querySelector('.btn__text');
    if (!txt) return;
    if (label) txt.textContent = label;
    else if (!on) txt.innerHTML = submitHtml();
  }

  function postOrder(payload) {
    return fetch(CFG.API_BASE + '/api/create-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, body: j }; });
    });
  }

  function onOrderSuccess(payload, body) {
    checkoutCapture.stop();
    clearPersisted();
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
    // The order now lives in Shopify; the cart it came from is spent. Left
    // full, a back-navigation lands on the same form with the same items and a
    // verified card — one tap from an accidental duplicate order.
    try { fetch('/cart/clear.js', { method: 'POST' }); } catch (e) {}
    // The id create-order's Conversions API Purchase carried. Meta collapses a
    // browser and server event sharing (event_name, event_id) into ONE
    // conversion, but only when the id is passed as the OPTIONS argument.
    // Inside the data object it was silently ignored and sales counted twice
    // (found in the US 2026-09-24, in NZ and AU 2026-10-02). Meta keeps the
    // browser copy of a merged pair, so it must say exactly what the server
    // copy said: create-order returns that as metaPurchase. Wrapped, because
    // tracking must never stop the thank-you screen for an order that exists.
    try {
      var purchaseEventId = (body && (body.metaEventId || body.event_id)) || payload.event_id;
      metaTrack('Purchase', (body && (body.metaPurchase || body.meta_purchase)) || purchaseData(payload, body), { eventID: purchaseEventId });
    } catch (e) {}
    var dates = scheduleDates();
    var set = function (id, v) { var n = $(id); if (n) n.textContent = v; };
    var plan = (body && body.plan) || {};
    set('thankyou-deposit', money(plan.paidTodayCents || firstPaymentCents()));
    set('thankyou-installment', money(perInstallmentCents()));
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(plan.secondPaymentDate || '');
    var next = m ? new Date(+m[1], +m[2] - 1, +m[3]) : (dates[1] || null);
    set('thankyou-first-date', next ? fmtLong(next) : '—');
    var nextLine = $('thankyou-next'); if (nextLine) nextLine.hidden = !next;
    set('thankyou-email', payload.customer.email);
    state.paidIntentId = null;
    clearCheckoutSession();
    var overlay = $('thank-you-overlay');
    if (overlay) { overlay.hidden = false; window.scrollTo({ top: 0, behavior: 'smooth' }); }
  }

  var PROCESSING_MSG = 'Your bank is still confirming this payment. Wait a moment, then press the button again — you won\'t be charged twice.';

  /*
   * A charge request whose answer never came back (the connection dropped, the server
   * failed, the bank is still deciding) may have taken the money. Until a definite answer
   * arrives (paid, declined, refused), no message may say "Nothing has been charged" — the
   * dry run's refusals do, and they are true only when no charge was attempted. Kept in
   * the checkout session so a reload remembers it.
   */
  var CHARGE_UNKNOWN_NOTE = 'If your last payment attempt went through, you won\'t be charged twice — and it\'s refunded if your order isn\'t placed.';
  function setChargeUnknown(unknown) {
    state.chargeUnknown = unknown;
    saveCheckoutSession();
  }
  function honest(message) {
    var text = String(message || '');
    if (!state.chargeUnknown || !/nothing (has been|was|is) charged/i.test(text)) return text;
    var stripped = text.replace(/\s*Nothing (has been|was|is) charged\.?/gi, '').trim();
    return (stripped ? stripped + ' ' : '') + CHARGE_UNKNOWN_NOTE;
  }
  var PAID_NOT_PLACED = 'Your first payment went through, but we couldn\'t place your order yet. Press the button again — you won\'t be charged twice.';
  var CHARGE_REFUSED = {
    account_hold: 'We can\'t start a new layaway online right now. Please email contact@laybyusa.com and we\'ll help.',
    too_many_attempts: 'That\'s a lot of tries for one checkout. Please wait a few minutes, or email contact@laybyusa.com.',
    checkout_expired: 'This checkout timed out and its payment was refunded. Press the button again to start a fresh one.'
  };

  /*
   * The checkout's payment was refunded (no order came of it): that checkout is over. Forget
   * it and start a fresh one — a new checkout id, so the next press takes a new first payment.
   */
  function startFreshCheckout() {
    state.checkoutEventId = 'lw-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    state.paidIntentId = null;
    state.chargeUnknown = false;
    saveCheckoutSession();
    try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
  }

  /*
   * This checkout must never talk to a create-order that predates it: that backend ignores
   * dry_run, so the "check first" call below would place a real order. Its health report
   * carries paymentGate only from the pay-at-checkout deploy — and only that deploy answers
   * this page's origin — so until it is live (or on a theme preview against the old backend)
   * the page says checkout is being updated. A "ready" answer is kept; a failed check is
   * asked again on the next press.
   */
  var BACKEND_UPDATING = 'Checkout is being updated right now. Please refresh the page in a few minutes and try again. Nothing has been charged.';
  var backendConfirmed = false;
  function backendReady() {
    if (backendConfirmed) return Promise.resolve(true);
    return fetch(CFG.API_BASE + '/api/health', { method: 'GET' })
      .then(function (r) { return r.json(); })
      .then(function (j) { backendConfirmed = Boolean(j && j.paymentGate); return backendConfirmed; })
      .catch(function () { return false; });
  }

  /** create-order as a dry run: every server check, nothing created, the first payment computed. */
  function preflight(payload) {
    var dry = JSON.parse(JSON.stringify(payload));
    dry.dry_run = true;
    return postOrder(dry);
  }

  /** The SERVER charges the first payment, with this attempt's ConfirmationToken. */
  function chargeFirstPayment(tokenId, amountCents, email) {
    return fetch(CFG.CARD_API_BASE + '/api/stripe/checkout-charge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        confirmationTokenId: tokenId,
        eventId: state.checkoutEventId,
        email: email,
        amountCents: amountCents,
        returnUrl: window.location.origin + window.location.pathname + '?payment_return=1'
      })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) { return { status: r.status, body: j }; });
    });
  }

  /** Paid: create the order against the charge. A failure here never charges again. */
  function placeOrder(payload) {
    payload.formDetails.stripe_payment_intent_id = state.paidIntentId;
    try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload)); } catch (err) {}
    setSubmitting(true, 'Starting your plan…');
    return postOrder(payload).then(function (res) {
      if (res.ok && res.body && res.body.success !== false) { onOrderSuccess(payload, res.body); return; }
      setSubmitting(false);
      if (res.body && res.body.error === 'PAYMENT_REFUNDED') startFreshCheckout();
      showFormErrors([(res.body && res.body.message) || PAID_NOT_PLACED]);
    }).catch(function () {
      setSubmitting(false);
      showFormErrors([PAID_NOT_PLACED]);
    });
  }

  function paid(intentId, payload) {
    state.paidIntentId = intentId;
    saveCheckoutSession();
    return placeOrder(payload);
  }

  /**
   * What the charge endpoint said, turned into the next step or a message. doubtBefore is
   * whether an EARLIER attempt was in doubt, for answers that say nothing about the past.
   */
  function settleCharge(charge, payload, doubtBefore) {
    var b = charge.body || {};
    if (charge.status === 200 && b.status === 'succeeded') { setChargeUnknown(false); return paid(b.paymentIntentId, payload); }
    if (charge.status === 200 && b.status === 'requires_action' && b.clientSecret) {
      setSubmitting(true, 'Confirming with your bank…');
      return state.stripe.handleNextAction({ clientSecret: b.clientSecret }).then(function (r) {
        if (r.error) {
          setSubmitting(false);
          showCardError(friendlyCardError(r.error));
          // The bank's check failed or was abandoned: not paid, so offer another way. After a
          // lost connection the check may have passed and the payment gone through — the
          // doubt stays, and the next press finds out (never a second charge).
          if (definitelyNotPaid(r.error)) { setChargeUnknown(false); showPayFork(); }
          return;
        }
        var pi = r.paymentIntent;
        if (pi && pi.status === 'succeeded') { setChargeUnknown(false); return paid(pi.id, payload); }
        setSubmitting(false);
        if (pi && pi.status === 'processing') { showCardError(PROCESSING_MSG); return; }
        setChargeUnknown(false);
        showCardError((pi && pi.last_payment_error && friendlyCardError(pi.last_payment_error)) || DECLINED);
        showPayFork();
      });
    }
    setSubmitting(false);
    if (charge.status === 202 || b.status === 'processing') { showCardError(PROCESSING_MSG); return; }
    // A decline that names the checkout's intent is a definite answer: it was not paid. A
    // refusal made before the server looked at the intent (prepaid, not a card, account hold,
    // too many tries) charged nothing THIS time and says nothing about an earlier attempt, so
    // the doubt goes back to what it was before this press.
    if (b.status === 'declined') {
      setChargeUnknown(b.paymentIntentId ? false : doubtBefore === true);
      // A prepaid card (or anything that is not a card) is never steered to the bank choice,
      // where a card saved in Link goes unchecked: the choice goes away for this checkout.
      if (b.code === 'prepaid_card' || b.code === 'card_only') withdrawBankChoice();
      showCardError(declineCopy(b.code));
      showPayFork();
      return;
    }
    if (b.status === 'refused') {
      if (b.code === 'checkout_expired') startFreshCheckout();
      else setChargeUnknown(doubtBefore === true);
      showFormErrors([CHARGE_REFUSED[b.code] || DECLINED]);
      return;
    }
    showCardError(honest(b.error || 'We couldn\'t take the payment. Please try again — this checkout can never be charged twice.'));
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (!form.layawayReady || state.submitting || state.emptyCart) return;

    clearErrors();
    var allOk = [1, 2, 3, 4].every(function (n) { return validateStep(n); }) && (!giftOn() || validateStep('gift'));
    if (!allOk || !checkStateGate() || !checkGiftStateGate()) {
      showFormErrors(['Please fix the highlighted fields before you continue.']);
      return;
    }
    if (PLAN.order_cap_cents && (PLAN.price_cents || 0) > PLAN.order_cap_cents) {
      showFormErrors(['Your order is above our current limit of ' + money(PLAN.order_cap_cents) + '. Remove an item to continue.']);
      return;
    }

    var payload = buildPayload();

    // Already paid — a re-press after the order hit a snag. Straight to the order.
    if (state.paidIntentId) { placeOrder(payload); return; }

    if (!state.stripe || !state.elements) {
      mountStripe();
      showFormErrors(['Payment details aren\'t ready yet. Give it a moment and try again.']);
      return;
    }

    showCardError('');
    hidePayFork();
    setSubmitting(true, 'Checking your order…');
    // 0. The payment form FIRST, inside the tap itself: Apple Pay and Google Pay open their
    //    sheet only from a user gesture, and any network call before this loses it. It also
    //    checks the card fields, so an unfinished card never costs a server call. Called
    //    here and now, never behind a promise; a synchronous throw joins the catch below.
    var payForm = activeElements();
    var submitted;
    try { submitted = payForm.submit(); } catch (err) { submitted = Promise.reject(err); }
    Promise.resolve(submitted).then(function (sub) {
      if (sub && sub.error) { setSubmitting(false); showCardError(friendlyCardError(sub.error)); return null; }
      // 1. Only a backend that takes paid orders (see backendReady).
      return backendReady().then(function (ready) {
        if (ready) return preflight(payload);
        setSubmitting(false);
        showFormErrors([honest(BACKEND_UPDATING)]);
        return null;
      });
    // 2. Every server check BEFORE money moves, and the server's own first-payment figure.
    }).then(function (pre) {
      if (!pre) return null;
      if (!pre.ok || !pre.body || pre.body.success === false || !pre.body.dryRun) {
        setSubmitting(false);
        showFormErrors([honest((pre.body && pre.body.message) || 'We couldn\'t start your plan. Nothing has been charged. Please try again.')]);
        return null;
      }
      var amount = pre.body.firstPaymentCents;
      if (!amount || amount !== firstPaymentCents()) {
        setSubmitting(false);
        replan(window.cartData);
        showFormErrors([honest('Your total changed while you were checking out. Please review it and press the button again. Nothing has been charged.')]);
        return null;
      }
      // The dates signed must be the dates billed. The server keeps the payment #2 this page
      // showed when it can; when it cannot (a page left open a day, a device clock that is off),
      // the plan is redrawn from the server's checkout day and the customer checks it again.
      var serverPlan = pre.body.plan || {};
      if (state.shownSecond && serverPlan.secondPaymentDate && serverPlan.secondPaymentDate !== state.shownSecond) {
        var day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(serverPlan.firstPaymentDate || ''));
        if (day) state.serverDay = new Date(+day[1], +day[2] - 1, +day[3]);
        setSubmitting(false);
        renderSchedule();
        showFormErrors([honest('Your payment dates changed while you were checking out. Please check them and press the button again. Nothing has been charged.')]);
        return null;
      }
      setSubmitting(true, 'Taking your first payment…');
      // 3. The payment details, as a one-time token — no charge yet.
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload)); } catch (err) {}
      return state.stripe.createConfirmationToken({
        elements: payForm,
        params: { payment_method_data: { billing_details: billingDetails(payload) } }
      }).then(function (ct) {
        if (!ct) return null;
        if (ct.error) { setSubmitting(false); showCardError(friendlyCardError(ct.error)); return null; }
        state.lastMethod = methodOf(ct.confirmationToken.payment_method_preview);
        // 4. The server charges it (one charge per checkout, ever), then 5. the order.
        // From here until a definite answer, the money may have moved.
        var doubtBefore = state.chargeUnknown === true;
        setChargeUnknown(true);
        return chargeFirstPayment(ct.confirmationToken.id, amount, payload.customer.email)
          .then(function (charge) { return settleCharge(charge, payload, doubtBefore); });
      });
    }).catch(function (err) {
      setSubmitting(false);
      showCardError(isBlockedConnection(err)
        ? BLOCKED_SYSTEM
        : 'We couldn\'t take the payment. Please try again — this checkout can never be charged twice.');
    });
  });

  /* ------------------------------------------------- 3DS redirect return */

  /**
   * Back from a bank's 3-D Secure page (the server confirmed with return_url; most banks
   * finish inside handleNextAction instead). Stripe appends payment_intent and its client
   * secret. Succeeded → place the order from the stashed payload; still processing → say
   * so; anything else → nothing was charged, try again.
   */
  function handleStripeReturn() {
    var params = new URLSearchParams(window.location.search);
    var clean = function () { history.replaceState({}, '', window.location.pathname); };
    if (params.get('setup_intent')) {
      // A $0 card check started before the checkout changed (2026-10-05).
      clean();
      goToStep(4);
      showCardError('Checkout has just been updated. Please enter your card again and press the button to place your order.');
      return;
    }
    var secret = params.get('payment_intent_client_secret');
    if (!params.get('payment_intent') || !secret) return;
    var pending = null;
    try { pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch (e) {}

    withStripe(function () {
      var pk = null;
      try { pk = sessionStorage.getItem(PK_KEY); } catch (e) {}
      var stripe = window.Stripe(pk || CFG.STRIPE_PK);
      stripe.retrievePaymentIntent(secret).then(function (res) {
        clean();
        goToStep(4);
        var intent = res.paymentIntent;
        if (intent && intent.status === 'succeeded') {
          state.paidIntentId = intent.id;
          saveCheckoutSession();
          if (pending) { placeOrder(pending); return; }
          showFormErrors(
            ['Your first payment went through. Check your details and press the button to finish your order — you won\'t be charged again.'],
            'One more step'
          );
          return;
        }
        if (intent && intent.status === 'processing') { showCardError(PROCESSING_MSG); return; }
        try { sessionStorage.removeItem(PENDING_KEY); } catch (e) {}
        showCardError('Your bank didn\'t complete the check, so nothing was charged. Please try again or use a different card.');
      }).catch(clean);
    });
  }

  /* ------------------------------------------------------- exit intent */

  function bindExitIntent() {
    var shown = false;
    try { shown = sessionStorage.getItem(EXIT_KEY) === '1'; } catch (e) {}
    if (shown) return;
    document.addEventListener('mouseout', function (e) {
      if (e.clientY > 0 || shown) return;
      var email = ($('order-form-register-email') || {}).value || '';
      if (!RE_EMAIL.test(email)) return;
      shown = true;
      try { sessionStorage.setItem(EXIT_KEY, '1'); } catch (err) {}
      showFormErrors(
        ['Your progress is saved on this device — come back any time and pick up where you left off.'],
        'Saved for later'
      );
    });
  }

  /* ------------------------------------------------------ cart changes */

  /**
   * The plan JSON was rendered by Liquid for the cart as it was at page load.
   * cart.js re-renders the items block on every quantity change or remove and
   * then fires cart:updated with the new cart; this re-derives the plan from
   * the new total using the same arithmetic as snippets/layaway-plan.liquid,
   * so the schedule, the authorization and the totals all move together.
   *
   * PLAN is mutated in place on purpose: dozens of functions read PLAN.x and
   * re-binding a fresh object would mean threading it through every one.
   */
  function replan(cart) {
    if (!cart || typeof cart.total_price !== 'number') return;
    var p = Math.max(0, Math.round(cart.total_price));
    var pct = PLAN.deposit_pct || 0;
    var dep = 0;
    if (pct > 0) {
      dep = Math.floor(p * pct / 100);
      if (dep < (PLAN.deposit_floor_cents || 0)) dep = PLAN.deposit_floor_cents || 0;
      dep = Math.ceil(dep / 100) * 100;
      if (dep > p) dep = p;
    }
    var inst = state.weeklyRateCents || PLAN.weekly_base_cents || PLAN.installment_cents || 0;
    var rem = p - dep;
    PLAN.price_cents = p;
    PLAN.deposit_cents = dep;
    PLAN.installment_cents = inst;
    PLAN.weekly_equiv_cents = inst;
    PLAN.weeks = (rem <= 0 || inst <= 0) ? 0 : Math.ceil(rem / inst);

    window.cartData = cart;
    var sub = $('cart-subtotal');
    if (sub && typeof cart.items_subtotal_price === 'number') sub.textContent = money(cart.items_subtotal_price);

    state.capBlocked = !!(PLAN.order_cap_cents && p > PLAN.order_cap_cents);
    state.emptyCart = p <= 0 || !cart.items || !cart.items.length;
    var w = $('order-cap-warning');
    if (w) w.hidden = !state.capBlocked;
    applySubmitBlocks();

    updateSummaryTotals();
    renderSchedule();
    maybeQuoteTax();
  }

  document.addEventListener('cart:updated', function (e) {
    replan(e && e.detail && e.detail.cart);
    checkoutCapture.schedule();
  });

  /* ------------------------------------------------------------- init */

  /**
   * The authorization block quotes the customer's own name and dates back to
   * them. It used to be rendered once, on entering step 4; on one page it has
   * to keep up with the fields above it.
   */
  var summaryTimer = null;
  function scheduleSummary() {
    clearTimeout(summaryTimer);
    summaryTimer = setTimeout(renderFinalSummary, 200);
  }

  function init() {
    checkoutSession();
    restore();
    try {
      var saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (saved.__gift && $('gift-toggle')) $('gift-toggle').checked = true;
    } catch (e) {}
    syncGift();
    checkStateGate();
    renderRateChips();
    var cart = window.cartData;
    if (!cart || !Number.isFinite(cart.total_price) || !Array.isArray(cart.items)) {
      throw new Error('Checkout cart data is missing or invalid');
    }
    replan(cart);
    renderFinalSummary();
    bindExitIntent();
    handleStripeReturn();
    if (state.paidIntentId && !new URLSearchParams(location.search).get('payment_intent')) {
      showFormErrors(
        ['Your first payment already went through. Check your details and press the button to finish your order \u2014 you won\'t be charged again.'],
        'Almost done'
      );
    }
    if (PLAN.order_cap_cents && (PLAN.price_cents || 0) > PLAN.order_cap_cents) {
      var w = $('order-cap-warning');
      if (w) w.hidden = false;
      state.capBlocked = true;
      applySubmitBlocks();
    }
    // The canvas is visible from the first paint now, so it can size itself.
    if (sigPad) sigPad.resize();
    form.addEventListener('input', scheduleSummary);
    form.addEventListener('change', scheduleSummary);
    // Stripe used to mount on entering step 4. It refuses to mount without a
    // valid email and returns without arming itself, so it is safe to try on
    // every keystroke and it arms the moment the address is complete.
    var email = $('order-form-register-email');
    if (email) {
      email.addEventListener('input', mountStripe);
      email.addEventListener('blur', mountStripe);
      if (email.value) mountStripe();
    }
  }

  function initialize() {
    if (form.layawayAbandoned) return;
    try {
      init();
      window.LayawayForm = {
        form: form, state: state, plan: PLAN, goToStep: goToStep, buildPayload: buildPayload, cardError: friendlyCardError,
        firstPaymentCents: firstPaymentCents, scheduleDates: scheduleDates
      };
      form.layawayReady = true;
      applySubmitBlocks();
      form.dispatchEvent(new Event('layaway:ready'));
    } catch (err) {
      if (form.layawayGuard) form.layawayGuard.fail();
      console.error('[layaway] initialization failed', err);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
